import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import { Radio, RadioChangeEvent, Tabs } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import Administration from '../../Constants/Endpoints/Administration';
import BITargetPurgeRequestModel from '../../Interfaces/Requests/Reports/BITargetPurgeRequestModel';
import GetFTPServerAdminModelsRequest from '../../Interfaces/Requests/Administration/GetFTPServerAdminModelsRequest';
import GetFTPServerAdminModelsResponse from '../../Interfaces/Responses/Administration/GetFTPServerAdminModelResponse';
import GetTenantAdminModelsRequest from '../../Interfaces/Requests/Administration/GetTenantAdminModelsRequest';
import GetTenantAdminModelsResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelResponse';
import GetReportAdminModelsRequest from '../../Interfaces/Requests/Administration/GetReportAdminModelsRequest';
import GetReportAdminModelsResponse from '../../Interfaces/Responses/Administration/GetReportAdminModelResponse';
import GetChartAdminModelsRequest from '../../Interfaces/Requests/Administration/GetChartAdminModelsRequest';
import GetChartAdminModelsResponse from '../../Interfaces/Responses/Administration/GetChartAdminModelResponse';
import GetDataSetAdminModelsRequest from '../../Interfaces/Requests/Administration/GetDataSetAdminModelsRequest';
import GetDataSetAdminModelsResponse from '../../Interfaces/Responses/Administration/GetDataSetAdminModelResponse';
import GetReportLinkageTraceRowsModelRequest from '../../Interfaces/Requests/Administration/GetReportLinkageTraceRowsModelRequest';
import GetReportLinkageTraceRowsModelResponse from '../../Interfaces/Responses/Administration/GetReportLinkageTraceRowsModelResponse';
import GetAISingleTurnPromptModelRequest from '../../Interfaces/Requests/AI/GetAISingleTurnPromptModelRequest';
import GetAISingleTurnPromptModelResponse from '../../Interfaces/Responses/AI/GetAISingleTurnPromptModelResponse';
import GetAIMultiTurnPromptModelRequest, { GetAIMultiTurnPromptMessageModel } from '../../Interfaces/Requests/AI/GetAIMultiTurnPromptModelRequest';
import GetAIMultiTurnPromptModelResponse from '../../Interfaces/Responses/AI/GetAIMultiTurnPromptModelResponse';
import GetAIProviderListModelRequest from '../../Interfaces/Requests/AI/GetAIProviderListModelRequest';
import GetAIProviderListModelResponse from '../../Interfaces/Responses/AI/GetAIProviderListModelResponse';
import GetAIProviderStatusModelRequest from '../../Interfaces/Requests/AI/GetAIProviderStatusModelRequest';
import GetAIProviderStatusModelResponse from '../../Interfaces/Responses/AI/GetAIProviderStatusModelResponse';
import {
    mapSlimChartAdminModel,
    mapSlimDataSetAdminModel,
    mapSlimFTPServerAdminModel,
    SlimChartAdminModel,
    SlimDataSetAdminModel,
    SlimFTPServerAdminModel
} from '../Shared/ManagedHelpers';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import GetTargetPurgeSummaryModelRequest from '../../Interfaces/Requests/Administration/GetTargetPurgeSummaryModelRequest';
import GetTargetPurgeSummaryModelResponse from '../../Interfaces/Responses/Administration/GetTargetPurgeSummaryModelResponse';
import GetRecordCleanupLocatorSummaryModelRequest from '../../Interfaces/Requests/Administration/GetRecordCleanupLocatorSummaryModelRequest';
import GetRecordCleanupLocatorSummaryModelResponse from '../../Interfaces/Responses/Administration/GetRecordCleanupLocatorSummaryModelResponse';
import GetReportDataSetInputScanSummaryModelResponse from '../../Interfaces/Responses/Administration/GetReportDataSetInputScanSummaryModelResponse';
import GetReportDataSetInputScanSummaryModelRequest from '../../Interfaces/Requests/Administration/GetReportDataSetInputScanSummaryModelRequest';
import {
    Alert as ServaAlert,
    Button,
    Card,
    Checkbox,
    Col,
    Collapse,
    Divider,
    Form,
    Input,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import MultiStepJobViewer, { MultiStepEvent } from '../Shared/MultiStepJobViewer';
import { SimplifiedTenant } from '../Shared/SimplifiedTenant';
import { SimplifiedReport } from '../Shared/SimplifiedReport';

type RetainBIMaintenanceProps = {};

type BIPurgeTargetType = 'report' | 'chart' | 'dataset' | 'ftpserver' | 'tenant';
type RetainBIMaintenanceTabKey =
    'target-purge' |
    'record-cleanup' |
    'report-dataset-input-scanning' |
    'report-linkage-trace' |
    'ai-provider' ;

const RetainBIMaintenance: React.FC<RetainBIMaintenanceProps> = () => {
    const { user } = useAuth();
    const [formRetainBIMaintenance] = Form.useForm();

    const queryClient = useQueryClient();

    const biReportDataSetInputScanMode = Form.useWatch('biReportDataSetInputScanMode', formRetainBIMaintenance);
    const biReportDataSetInputReportID = Form.useWatch('biReportDataSetInputReportID', formRetainBIMaintenance);

    const [reports, setReports] = useState<SimplifiedReport[]>([]);
    const [tenants, setTenants] = useState<SimplifiedTenant[]>([]);
    const [charts, setCharts] = useState<SlimChartAdminModel[]>([]);
    const [dataSets, setDataSets] = useState<SlimDataSetAdminModel[]>([]);
    const [ftpServers, setFTPServers] = useState<SlimFTPServerAdminModel[]>([]);

    const [biSelectedTenantID, setBISelectedTenantID] = useState(0);

    const [biPurgeTargetType, setBIPurgeTargetType] = useState<BIPurgeTargetType>('report');
    const [biSelectedTargetID, setBISelectedTargetID] = useState('');
    const [biSelectedTargetGroupName, setBISelectedTargetGroupName] = useState('');
    const [biSelectedTargetName, setBISelectedTargetName] = useState('');

    const [biSelectedReportID, setBISelectedReportID] = useState('');

    const [isTargetPurgeButtonDisabled, setIsTargetPurgeButtonDisabled] = useState(true);
    const [targetPurgeSummary, setTargetPurgeSummary] = useState<GetTargetPurgeSummaryModelResponse | null>(null);
    const [isTargetPurgePreviewOnly, setIsTargetPurgePreviewOnly] = useState(true);

    const [isRecordCleanupLocatorButtonDisabled, setIsRecordCleanupLocatorButtonDisabled] = useState(true);
    const [recordCleanupLocatorSummary, setRecordCleanupLocatorSummary] = useState<GetRecordCleanupLocatorSummaryModelResponse | null>(null);

    const [isReportDataSetInputScanButtonDisabled, setIsReportDataSetInputScanButtonDisabled] = useState(false);
    const [reportDataSetInputScanSummary, setReportDataSetInputScanSummary] = useState<GetReportDataSetInputScanSummaryModelResponse | null>(null);
    
    const [isReportLinkageTraceButtonDisabled, setIsReportLinkageTraceButtonDisabled] = useState(true);
    const [reportLinkageTraceRows, setReportLinkageTraceRows] = useState<GetReportLinkageTraceRowsModelResponse[]>([]);

    const [activeMaintenanceTab, setActiveMaintenanceTab] = useState<RetainBIMaintenanceTabKey>('target-purge');

    const [aiProviderExternalVendorID, setAIProviderExternalVendorID] = useState('1');
    const [aiProviderStatus, setAIProviderStatus] = useState<GetAIProviderStatusModelResponse | null>(null);
    const [aiProviders, setAIProviders] = useState<GetAIProviderListModelResponse[]>([]);
    const [aiSingleTurnPrompt, setAISingleTurnPrompt] = useState('');
    const [aiSingleTurnPromptResponse, setAISingleTurnPromptResponse] = useState<GetAISingleTurnPromptModelResponse | null>(null);
    const [aiMultiTurnPrompt, setAIMultiTurnPrompt] = useState('');
    const [aiMultiTurnMessages, setAIMultiTurnMessages] = useState<GetAIMultiTurnPromptMessageModel[]>([]);
    const [aiMultiTurnPromptResponse, setAIMultiTurnPromptResponse] = useState<GetAIMultiTurnPromptModelResponse | null>(null);

    const [currentError, setCurrentError] = useState('');

    const { isLoading: isGetTenantsLoading, isError: isGetTenantsError, error: getTenantsError, refetch: refetchTenants } = useQuery<GetTenantAdminModelsResponse[], unknown>(
        ['ad-hoc-maintenance-tenants', false],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_TENANT_ADMIN_MODELS}`;
                const requestData: GetTenantAdminModelsRequest = { gta_includeDisabled: false, };
                return await AxiosUtil.get<GetTenantAdminModelsResponse[], GetTenantAdminModelsRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models AUD] Internal Error: ' + err);
                console.error('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models AUD] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models AUD] Internal Error: ' + err);
                console.error('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models AUD] Internal Error: ', err);
            },
            onSuccess: (response: GetTenantAdminModelsResponse[]) => {
                if (response) {
                    let simplifiedTenants: SimplifiedTenant[] = response
                        .map((eachElement) => {
                            return {
                                id: eachElement.id,
                                name: eachElement.name,
                                connString: eachElement.connString,
                                connStringRedacted: eachElement.connString,
                                logoImage: eachElement.logoImage,
                                nameGeneric: eachElement.nameGeneric,
                                hideFromDemo: eachElement.hideFromDemo,
                                sqlFirstDayOfWeek: eachElement.sqlFirstDayOfWeek,
                                applicationId: eachElement.applicationID,
                                linkedDbName: eachElement.linkedDbName,
                                urlBase: eachElement.urlBase,
                                encryptKey: eachElement.encryptKey,
                                encryptCert: eachElement.encryptCert,

                                userid: 0,

                                deleted: eachElement.deleted
                            };
                        })
                        .sort((a, b) => a.name.localeCompare(b.name));

                    simplifiedTenants = simplifiedTenants.filter(
                        (eachTenant) => !eachTenant.deleted
                    );

                    setTenants(simplifiedTenants);
                }
            }
        }
    );

    const { isLoading: isGetReportsLoading, isError: isGetReportsError, error: getReportsError, refetch: refetchReports } = useQuery<GetReportAdminModelsResponse[], unknown>(
        ['ad-hoc-maintenance-reports', false],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_REPORT_ADMIN_MODELS}`;
                const requestData: GetReportAdminModelsRequest = { gra_includeDisabled: false, };
                return await AxiosUtil.get<GetReportAdminModelsResponse[], GetReportAdminModelsRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_REPORT_ADMIN_MODELS: [Get Admin Reports] Internal Error: ' + err);
                console.error('GET_REPORT_ADMIN_MODELS: [Get Admin Reports] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('GET_REPORT_ADMIN_MODELS: [Get Admin Reports] Internal Error: ' + err);
                console.error('GET_REPORT_ADMIN_MODELS: [Get Admin Reports] Internal Error: ', err);
            },
            onSuccess: (response: GetReportAdminModelsResponse[]) => {
                if (response) {
                    const simplifiedReports: SimplifiedReport[] = response
                        .map((eachElement) => {
                            return {
                                reportId: eachElement.id,
                                reportName: eachElement.name,
                                reportGroupName: eachElement.groupName,
                                isDashboard: eachElement.isDashboard,
                                useDatasetInputs: eachElement.useDatasetInputs,
                                released: eachElement.released,
                                apiOnly: eachElement.apiOnly,
                                exportExcel: eachElement.exportExcel,
                                exportPDF: eachElement.exportPDF,
                                exportPassword: eachElement.exportPassword,

                                tenantId: eachElement.tenantID,
                                tenantName: eachElement.tenantName,

                                deleted: eachElement.deleted
                            };
                        })
                        .sort((a, b) => (a.reportGroupName + ' ' + a.reportName).localeCompare(b.reportGroupName + ' ' + b.reportName));

                    setReports(simplifiedReports);
                }
            }
        }
    );

    const { isLoading: isGetChartsLoading, isError: isGetChartsError, error: getChartsError, refetch: refetchCharts } = useQuery<GetChartAdminModelsResponse[], unknown>(
        ['ad-hoc-maintenance-charts', false],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_CHART_ADMIN_MODELS}`;
                const requestData: GetChartAdminModelsRequest = { gca_includeDisabled: false };
                return await AxiosUtil.get<GetChartAdminModelsResponse[], GetChartAdminModelsRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_CHART_ADMIN_MODELS: [Get Chart Admin Models AUD] Internal Error: ' + err);
                console.error('GET_CHART_ADMIN_MODELS: [Get Chart Admin Models AUD] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('GET_CHART_ADMIN_MODELS: [Get Chart Admin Models AUD] Internal Error: ' + err);
                console.error('GET_CHART_ADMIN_MODELS: [Get Chart Admin Models AUD] Internal Error: ', err);
            },
            onSuccess: (response: GetChartAdminModelsResponse[]) => {
                const slimCharts = (response ?? [])
                    .map(mapSlimChartAdminModel)
                    .filter((eachChart) => !eachChart.deleted)
                    .sort((a, b) => a.title.localeCompare(b.title));

                setCharts(slimCharts);
            }
        }
    );

    const { isLoading: isGetDataSetsLoading, isError: isGetDataSetsError, error: getDataSetsError, refetch: refetchDataSets } = useQuery<GetDataSetAdminModelsResponse[], unknown>(
        ['ad-hoc-maintenance-datasets', false],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_DATASET_ADMIN_MODELS}`;
                const requestData: GetDataSetAdminModelsRequest = { gdsa_includeDisabled: false };
                return await AxiosUtil.get<GetDataSetAdminModelsResponse[], GetDataSetAdminModelsRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models AUD] Internal Error: ' + err);
                console.error('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models AUD] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models AUD] Internal Error: ' + err);
                console.error('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models AUD] Internal Error: ', err);
            },
            onSuccess: (response: GetDataSetAdminModelsResponse[]) => {
                const slimDataSets = (response ?? [])
                    .map(mapSlimDataSetAdminModel)
                    .filter((eachDataSet) => !eachDataSet.deleted)
                    .sort((a, b) => a.name.localeCompare(b.name));

                setDataSets(slimDataSets);
            }
        }
    );

    const { isLoading: isGetFTPServersLoading, isError: isGetFTPServersError, error: getFTPServersError, refetch: refetchFTPServers } = useQuery<GetFTPServerAdminModelsResponse[], unknown>(
        ['ad-hoc-maintenance-ftpservers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_FTPSERVER_ADMIN_MODELS}`;
                const requestData: GetFTPServerAdminModelsRequest = {};
                return await AxiosUtil.get<GetFTPServerAdminModelsResponse[], GetFTPServerAdminModelsRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_FTPSERVER_ADMIN_MODELS: [Get FTP Server Admin Models AUD] Internal Error: ' + err);
                console.error('GET_FTPSERVER_ADMIN_MODELS: [Get FTP Server Admin Models AUD] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('GET_FTPSERVER_ADMIN_MODELS: [Get FTP Server Admin Models AUD] Internal Error: ' + err);
                console.error('GET_FTPSERVER_ADMIN_MODELS: [Get FTP Server Admin Models AUD] Internal Error: ', err);
            },
            onSuccess: (response: GetFTPServerAdminModelsResponse[]) => {
                const slimFTPServers = (response ?? [])
                    .map(mapSlimFTPServerAdminModel)
                    .sort((a, b) => a.name.localeCompare(b.name));

                setFTPServers(slimFTPServers);
            }
        }
    );

    const refetchBIPurgeTargetLists = () => {
        void refetchTenants();
        void refetchReports();
        void refetchCharts();
        void refetchDataSets();
        void refetchFTPServers();

        if (biPurgeTargetType === 'report' || biPurgeTargetType === 'tenant') {
            invalidateReportMenu(queryClient);
        }
    };

    const { mutate: getBITargetPurgeSummary, isError: isGetBITargetPurgeSummaryError, error: getBITargetPurgeSummaryError, reset: resetGetBITargetPurgeSummary } = useMutation(
        async ({ jobId }: GetTargetPurgeSummaryModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/Workflows/${jobId}/target-purge-summary`;
                const requestData: GetTargetPurgeSummaryModelRequest = { jobId: jobId };

                const responseData = await AxiosUtil.get<GetTargetPurgeSummaryModelResponse, GetTargetPurgeSummaryModelRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('BI_TARGET_PURGE_SUMMARY: [BI Target Purge Summary] Internal Error: ' + err);
                console.error('BI_TARGET_PURGE_SUMMARY: [BI Target Purge Summary] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('BI_TARGET_PURGE_SUMMARY: [BI Target Purge Summary] Internal Error: ' + err);
                console.error('BI_TARGET_PURGE_SUMMARY: [BI Target Purge Summary] Internal Error: ', err);
            },
            onSuccess: (response: GetTargetPurgeSummaryModelResponse) => {
                setTargetPurgeSummary(response);
            }
        }
    );

    const { mutate: getBIRecordCleanupLocatorSummary, isError: isGetBIRecordCleanupLocatorSummaryError, error: getBIRecordCleanupLocatorSummaryError, reset: resetGetBIRecordCleanupLocatorSummary } = useMutation(
        async ({ jobId }: GetRecordCleanupLocatorSummaryModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/Workflows/${jobId}/record-cleanup-locator-summary`;
                const requestData: GetRecordCleanupLocatorSummaryModelRequest = { jobId: jobId };

                const responseData = await AxiosUtil.get<GetRecordCleanupLocatorSummaryModelResponse, GetRecordCleanupLocatorSummaryModelRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('BI_RECORD_CLEANUP_LOCATOR_SUMMARY: [BI Record Cleanup Locator Summary] Internal Error: ' + err);
                console.error('BI_RECORD_CLEANUP_LOCATOR_SUMMARY: [BI Record Cleanup Locator Summary] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('BI_RECORD_CLEANUP_LOCATOR_SUMMARY: [BI Record Cleanup Locator Summary] Internal Error: ' + err);
                console.error('BI_RECORD_CLEANUP_LOCATOR_SUMMARY: [BI Record Cleanup Locator Summary] Internal Error: ', err);
            },
            onSuccess: (response: GetRecordCleanupLocatorSummaryModelResponse) => {
                setRecordCleanupLocatorSummary(response);
            }
        }
    );

    const { mutate: getBIReportDataSetInputScanSummary, isError: isGetBIReportDataSetInputScanSummaryError, error: getBIReportDataSetInputScanSummaryError, reset: resetGetBIReportDataSetInputScanSummary } = useMutation(
        async ({ jobId }: GetReportDataSetInputScanSummaryModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/Workflows/${jobId}/report-dataset-input-scan-summary`;
                const requestData: GetReportDataSetInputScanSummaryModelRequest = { jobId: jobId };

                const responseData = await AxiosUtil.get<GetReportDataSetInputScanSummaryModelResponse, GetReportDataSetInputScanSummaryModelRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('BI_REPORT_DATASET_INPUT_SCAN_SUMMARY: [BI Report DataSet Input Scan Summary] Internal Error: ' + err);
                console.error('BI_REPORT_DATASET_INPUT_SCAN_SUMMARY: [BI Report DataSet Input Scan Summary] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetReportDataSetInputScanSummaryModelResponse) => {
                setReportDataSetInputScanSummary(response);
            },
            onError: (err: unknown) => {
                console.error('BI_REPORT_DATASET_INPUT_SCAN_SUMMARY: [BI Report DataSet Input Scan Summary] Internal Error:', err);
            }
        }
    );

    const { mutate: getReportLinkageTraceRows, isLoading: isGetReportLinkageTraceRowsLoading, isError: isGetReportLinkageTraceRowsError, error: getReportLinkageTraceRowsError, reset: resetGetReportLinkageTraceRows } = useMutation(
        async ({ jobId }: GetReportLinkageTraceRowsModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/Workflows/${jobId}/report-linkage-trace-rows`;
                const requestData: GetReportLinkageTraceRowsModelRequest = { jobId: jobId };

                const responseData = await AxiosUtil.get<GetReportLinkageTraceRowsModelResponse[], GetReportLinkageTraceRowsModelRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('REPORT_LINKAGE_TRACE_ROWS: [Report Linkage Trace Rows] Internal Error: ' + err);
                console.error('REPORT_LINKAGE_TRACE_ROWS: [Report Linkage Trace Rows] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetReportLinkageTraceRowsModelResponse[]) => {
                setReportLinkageTraceRows(response ?? []);
            },
            onError: (err: unknown) => {
                setCurrentError('REPORT_LINKAGE_TRACE_ROWS: [Report Linkage Trace Rows] Internal Error: ' + err);
                console.error('REPORT_LINKAGE_TRACE_ROWS: [Report Linkage Trace Rows] Internal Error: ', err);
            }
        }
    );

    const { mutate: getAIProviderStatus, isLoading: isGetAIProviderStatusLoading, isError: isGetAIProviderStatusError, error: getAIProviderStatusError, reset: resetGetAIProviderStatus } = useMutation(
        async ({ gaps_externalVendorID }: GetAIProviderStatusModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviderStatus`;
                const requestData: GetAIProviderStatusModelRequest = { gaps_externalVendorID: gaps_externalVendorID };

                const responseData = await AxiosUtil.get<GetAIProviderStatusModelResponse, GetAIProviderStatusModelRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('AI_PROVIDER_STATUS: [AI Provider Status] Internal Error: ' + err);
                console.error('AI_PROVIDER_STATUS: [AI Provider Status] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAIProviderStatusModelResponse) => {
                setAIProviderStatus(response);
            },
            onError: (err: unknown) => {
                setCurrentError('AI_PROVIDER_STATUS: [AI Provider Status] Internal Error: ' + err);
                console.error('AI_PROVIDER_STATUS: [AI Provider Status] Internal Error: ', err);
            }
        }
    );

    const { mutate: getAISingleTurnPrompt, isLoading: isGetAISingleTurnPromptLoading, isError: isGetAISingleTurnPromptError, error: getAISingleTurnPromptError, reset: resetGetAISingleTurnPrompt } = useMutation(
        async ({ externalVendorID, tenantID, prompt }: GetAISingleTurnPromptModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AISingleTurnPrompt`;
                const requestData: GetAISingleTurnPromptModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    prompt: prompt
                };

                const responseData = await AxiosUtil.post<GetAISingleTurnPromptModelResponse, GetAISingleTurnPromptModelRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('AI_SINGLE_TURN_PROMPT: [AI SingleTurn Prompt] Internal Error: ' + err);
                console.error('AI_SINGLE_TURN_PROMPT: [AI SingleTurn Prompt] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAISingleTurnPromptModelResponse) => {
                setAISingleTurnPromptResponse(response);
            },
            onError: (err: unknown) => {
                setCurrentError('AI_SINGLE_TURN_PROMPT: [AI SingleTurn Prompt] Internal Error: ' + err);
                console.error('AI_SINGLE_TURN_PROMPT: [AI SingleTurn Prompt] Internal Error: ', err);
            }
        }
    );

    const { mutate: getAIMultiTurnPrompt, isLoading: isGetAIMultiTurnPromptLoading, isError: isGetAIMultiTurnPromptError, error: getAIMultiTurnPromptError, reset: resetGetAIMultiTurnPrompt } = useMutation(
        async ({ externalVendorID, tenantID, aiConversationID, messages }: GetAIMultiTurnPromptModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AIMultiTurnPrompt`;
                const requestData: GetAIMultiTurnPromptModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    aiConversationID: aiConversationID,
                    messages: messages
                };

                const responseData = await AxiosUtil.post<GetAIMultiTurnPromptModelResponse, GetAIMultiTurnPromptModelRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('AI_MULTI_TURN_PROMPT: [AI MultiTurn Prompt] Internal Error: ' + err);
                console.error('AI_MULTI_TURN_PROMPT: [AI MultiTurn Prompt] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAIMultiTurnPromptModelResponse) => {
                setAIMultiTurnPromptResponse(response);

                if (response.success && response.assistantMessage) {
                    setAIMultiTurnMessages((currentMessages) => [
                        ...currentMessages,
                        response.assistantMessage
                    ]);
                }
            },
            onError: (err: unknown) => {
                setCurrentError('AI_MULTI_TURN_PROMPT: [AI MultiTurn Prompt] Internal Error: ' + err);
                console.error('AI_MULTI_TURN_PROMPT: [AI MultiTurn Prompt] Internal Error: ', err);
            }
        }
    );

    const { isLoading: isGetAIProvidersLoading, isError: isGetAIProvidersError, error: getAIProvidersError } = useQuery<GetAIProviderListModelResponse[], unknown>(
        ['ai-providers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviders`;
                const requestData: GetAIProviderListModelRequest = {};

                const responseData = await AxiosUtil.get<GetAIProviderListModelResponse[], GetAIProviderListModelRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('AI_PROVIDERS: [Get AI Providers] Internal Error: ' + err);
                console.error('AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAIProviderListModelResponse[]) => {
                const providers = response ?? [];
                setAIProviders(providers);

                if (providers.length > 0 && !providers.some((provider) => provider.externalVendorID.toString() === aiProviderExternalVendorID)) {
                    setAIProviderExternalVendorID(providers[0].externalVendorID.toString());
                    setAIProviderStatus(null);
                    setAISingleTurnPromptResponse(null);
                    setAIMultiTurnPromptResponse(null);
                    setAIMultiTurnMessages([]);
                }
            },
            onError: (err: unknown) => {
                setCurrentError('AI_PROVIDERS: [Get AI Providers] Internal Error: ' + err);
                console.error('AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
            }
        }
    );

    const clearBIPurgeTargetSelection = () => {
        setBISelectedTenantID(0);

        setBISelectedTargetID('');
        setBISelectedTargetGroupName('');
        setBISelectedTargetName('');

        formRetainBIMaintenance.setFieldsValue({
            biPurgeTargetID: undefined,
            biPurgeReportGroupName: undefined,
            biPurgeTargetName: undefined,
            biPurgeReportAttributes: undefined,
            biPurgeTenantName: undefined
        });

        setIsTargetPurgeButtonDisabled(true);
    };

    const handleBIPurgeTargetTypeChange = (e: RadioChangeEvent) => {
        const selectedTargetType = e.target.value as BIPurgeTargetType;

        setBIPurgeTargetType(selectedTargetType);
        clearBIPurgeTargetSelection();

        formRetainBIMaintenance.setFieldValue('biPurgeTargetType', selectedTargetType);
    };

    const isBIPurgeReportTarget = biPurgeTargetType === 'report';
        const biPurgeTargetDisplayName =
            biPurgeTargetType === 'dataset'
                ? 'DataSet'
                : biPurgeTargetType === 'ftpserver'
                    ? 'FTP Server'
                    : biPurgeTargetType.charAt(0).toUpperCase() + biPurgeTargetType.slice(1);

    const reportOnlyLabelStyle: React.CSSProperties = {
        color: isBIPurgeReportTarget ? 'black' : 'gray',
        paddingTop: '8px',
        textAlign: 'right',
        verticalAlign: 'middle'
    };

    const reportOnlyTextStyle: React.CSSProperties = {
        color: isBIPurgeReportTarget ? 'black' : 'gray',
        float: 'left'
    };

    const handleBIChartSelectionChange = (option: number) => {
        const selectedChart = charts.find((chart) => chart.id === option);

        setBISelectedTenantID(0);

        setBIPurgeTargetType('chart');
        setBISelectedTargetID(selectedChart?.id.toString() ?? '');
        setBISelectedTargetGroupName('');
        setBISelectedTargetName(selectedChart?.title ?? '');

        formRetainBIMaintenance.setFieldValue('biPurgeTargetID', selectedChart?.id);
        formRetainBIMaintenance.setFieldValue('biPurgeReportGroupName', undefined);
        formRetainBIMaintenance.setFieldValue('biPurgeTargetName', selectedChart?.title);
        formRetainBIMaintenance.setFieldValue('biPurgeTenantName', undefined);
        formRetainBIMaintenance.setFieldValue('biPurgeReportAttributes', undefined);

        setIsTargetPurgeButtonDisabled(!selectedChart);
    };

    const handleBIDataSetSelectionChange = (option: number) => {
        const selectedDataSet = dataSets.find((dataSet) => dataSet.id === option);

        setBISelectedTenantID(0);

        setBIPurgeTargetType('dataset');
        setBISelectedTargetID(selectedDataSet?.id.toString() ?? '');
        setBISelectedTargetGroupName('');
        setBISelectedTargetName(selectedDataSet?.name ?? '');

        formRetainBIMaintenance.setFieldValue('biPurgeTargetID', selectedDataSet?.id);
        formRetainBIMaintenance.setFieldValue('biPurgeReportGroupName', undefined);
        formRetainBIMaintenance.setFieldValue('biPurgeTargetName', selectedDataSet?.name);
        formRetainBIMaintenance.setFieldValue('biPurgeTenantName', undefined);
        formRetainBIMaintenance.setFieldValue('biPurgeReportAttributes', undefined);

        setIsTargetPurgeButtonDisabled(!selectedDataSet);
    };

    const handleBIFTPServerSelectionChange = (option: number) => {
        const selectedFTPServer = ftpServers.find((ftpServer) => ftpServer.id === option);

        setBISelectedTenantID(0);

        setBIPurgeTargetType('ftpserver');
        setBISelectedTargetID(selectedFTPServer?.id.toString() ?? '');
        setBISelectedTargetGroupName('');
        setBISelectedTargetName(selectedFTPServer?.name ?? '');

        formRetainBIMaintenance.setFieldValue('biPurgeTargetID', selectedFTPServer?.id);
        formRetainBIMaintenance.setFieldValue('biPurgeReportGroupName', undefined);
        formRetainBIMaintenance.setFieldValue('biPurgeTargetName', selectedFTPServer?.name);
        formRetainBIMaintenance.setFieldValue('biPurgeTenantName', undefined);
        formRetainBIMaintenance.setFieldValue('biPurgeReportAttributes', undefined);

        setIsTargetPurgeButtonDisabled(!selectedFTPServer);
    };

    const handleReportLinkageTraceSelectionChange = (reportID: number) => {
        const selectedReport = reports.find((report) => report.reportId === reportID);

        setBISelectedReportID(selectedReport?.reportId.toString() ?? '');
        setReportLinkageTraceRows([]);

        formRetainBIMaintenance.setFieldValue('biReportTraceLinkageReportId', selectedReport?.reportId);
        formRetainBIMaintenance.setFieldValue('biReportTraceLinkageReportGroupName', selectedReport?.reportGroupName);
        formRetainBIMaintenance.setFieldValue('biReportTraceLinkageReportName', selectedReport?.reportName);

        setIsReportLinkageTraceButtonDisabled(!selectedReport);
    };

    const handleReportLinkageTraceWorkflowEnded = (jobId: string, terminalStatus: string) => {
        if (terminalStatus !== 'finished') {
            return;
        }

        getReportLinkageTraceRows({ jobId: jobId });
    };

    const groupReportLinkageTraceRowsBySection = () => {
        const groupedRows = reportLinkageTraceRows.reduce<Record<string, GetReportLinkageTraceRowsModelResponse[]>>(
            (currentGroups, traceRow) => {
                const sectionName = traceRow.sectionName || 'Other';

                if (!currentGroups[sectionName]) {
                    currentGroups[sectionName] = [];
                }

                currentGroups[sectionName].push(traceRow);

                return currentGroups;
            },
            {}
        );

        return Object.entries(groupedRows)
            .map(([sectionName, rows]) => ({
                sectionName,
                sectionSort: rows[0]?.sectionSort ?? 999999,
                rows: rows
                    .slice()
                    .sort((leftRow, rightRow) =>
                        leftRow.sectionSort - rightRow.sectionSort ||
                        leftRow.reportID - rightRow.reportID ||
                        (leftRow.reportPageID ?? 0) - (rightRow.reportPageID ?? 0) ||
                        (leftRow.reportItemID ?? 0) - (rightRow.reportItemID ?? 0) ||
                        leftRow.rowSort - rightRow.rowSort ||
                        (leftRow.entityID ?? 0) - (rightRow.entityID ?? 0)
                    )
            }))
            .sort((leftSection, rightSection) =>
                leftSection.sectionSort - rightSection.sectionSort ||
                leftSection.sectionName.localeCompare(rightSection.sectionName)
            );
    };

    const getReportLinkageTraceRowStyle = (traceRow: GetReportLinkageTraceRowsModelResponse): React.CSSProperties => {
        if (traceRow.isMissing) {
            return { backgroundColor: '#fff1f0' };
        }

        if (traceRow.isDeleted) {
            return { backgroundColor: '#f5f5f5', color: '#777' };
        }

        if (traceRow.warningText && traceRow.warningText.trim() !== '') {
            return { backgroundColor: '#fffbe6' };
        }

        return {};
    };

    const renderReportLinkageTraceEntityDetails = (traceRow: GetReportLinkageTraceRowsModelResponse) => {
        try {
            const entityDetails = JSON.parse(traceRow.entityJson || '{}') as Record<string, unknown>;
            const detailEntries = Object.entries(entityDetails);

            if (detailEntries.length === 0) {
                return (
                    <Text>
                        No entity details available.
                    </Text>
                );
            }

            return (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(180px, 260px) minmax(260px, 1fr)',
                        gap: '4px 12px',
                        padding: '8px 12px'
                    }}
                >
                    {
                        detailEntries.map(([detailKey, detailValue]) => (
                            <React.Fragment key={detailKey}>
                                <Text style={{fontWeight: 'bold'}}>
                                    {detailKey}
                                </Text>
                                <Text style={{wordBreak: 'break-word'}}>
                                    {
                                        detailValue === null || detailValue === undefined
                                            ? 'NULL'
                                            : typeof detailValue === 'object'
                                                ? JSON.stringify(detailValue)
                                                : String(detailValue)
                                    }
                                </Text>
                            </React.Fragment>
                        ))
                    }
                </div>
            );
        } catch {
            return (
                <div style={{padding: '8px 12px'}}>
                    <Text style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                        {traceRow.entityJson || 'No entity details available.'}
                    </Text>
                </div>
            );
        }
    };

    const handleBITenantSelectionChange = (option: number) => {
        const selectedTenant = tenants.find((tenant) => tenant.id === option);

        setBISelectedTenantID(selectedTenant?.id ?? 0);

        setBIPurgeTargetType('tenant');
        setBISelectedTargetID(selectedTenant?.id.toString() ?? '');
        setBISelectedTargetGroupName('');
        setBISelectedTargetName(selectedTenant?.name ?? '');

        formRetainBIMaintenance.setFieldValue('biPurgeTargetID', selectedTenant?.id);
        formRetainBIMaintenance.setFieldValue('biPurgeReportGroupName', undefined);
        formRetainBIMaintenance.setFieldValue('biPurgeTargetName', selectedTenant?.name);
        formRetainBIMaintenance.setFieldValue('biPurgeTenantName', selectedTenant?.name);
        formRetainBIMaintenance.setFieldValue('biPurgeReportAttributes', undefined);

        setIsTargetPurgeButtonDisabled(!selectedTenant);
    };

    const handleBIPurgeTargetSelectionChange = (option: number) => {
        if (biPurgeTargetType === 'chart') {
            handleBIChartSelectionChange(option);
        }
        else if (biPurgeTargetType === 'dataset') {
            handleBIDataSetSelectionChange(option);
        }
        else if (biPurgeTargetType === 'ftpserver') {
            handleBIFTPServerSelectionChange(option);
        }
        else if (biPurgeTargetType === 'tenant') {
            handleBITenantSelectionChange(option);
        }
        else {
            handleBIReportSelectionChange(option);
        }
    };

    const renderBIPurgeTargetOptions = () => {
        if (biPurgeTargetType === 'chart') {
            return charts.map((eachChart) => (
                <Option
                    key={`chart-${eachChart.id}`}
                    value={eachChart.id}
                    label={eachChart.title}
                >
                    {eachChart.title}
                </Option>
            ));
        }

        if (biPurgeTargetType === 'dataset') {
            return dataSets.map((eachDataSet) => (
                <Option
                    key={`dataset-${eachDataSet.id}`}
                    value={eachDataSet.id}
                    label={eachDataSet.name}
                >
                    {eachDataSet.name}
                </Option>
            ));
        }

        if (biPurgeTargetType === 'ftpserver') {
            return ftpServers.map((eachFTPServer) => (
                <Option
                    key={`ftpserver-${eachFTPServer.id}`}
                    value={eachFTPServer.id}
                    label={eachFTPServer.name}
                >
                    {eachFTPServer.name}
                </Option>
            ));
        }

        if (biPurgeTargetType === 'tenant') {
            return tenants.map((eachTenant) => (
                <Option
                    key={`tenant-${eachTenant.id}`}
                    value={eachTenant.id}
                    label={eachTenant.name}
                >
                    {eachTenant.name}
                </Option>
            ));
        }

        return reports.map((eachReport) => (
            <Option
                key={`report-${eachReport.reportId}`}
                value={eachReport.reportId}
                label={'[' + eachReport.reportGroupName + '] ' + eachReport.reportName}
            >
                {'[' + eachReport.reportGroupName + '] ' + eachReport.reportName}
            </Option>
        ));
    };

    const handleBIReportSelectionChange = (option: number) => {
        let selectedReport = reports.find((report) => (report.reportId === option));

        setBIPurgeTargetType('report');
        setBISelectedTargetID(selectedReport?.reportId.toString() ?? '');
        setBISelectedTargetGroupName(selectedReport?.reportGroupName ?? '');
        setBISelectedTargetName(selectedReport?.reportName ?? '');

        let selectedTenant = tenants.find((tenant) => (tenant.id === selectedReport?.tenantId));
        setBISelectedTenantID(selectedTenant?.id ?? 0);

        formRetainBIMaintenance.setFieldValue('biPurgeTargetID', selectedReport?.reportId);
        formRetainBIMaintenance.setFieldValue('biPurgeReportGroupName', selectedReport?.reportGroupName);
        formRetainBIMaintenance.setFieldValue('biPurgeTargetName', selectedReport?.reportName);
        formRetainBIMaintenance.setFieldValue('biPurgeTenantName', selectedTenant?.name);

        let selectedAttributes = (selectedReport?.apiOnly ? 'API; ' : '') +
                                 (selectedReport?.released ? 'Released; ' : '') +
                                 (selectedReport?.isDashboard ? 'DashBoard; ' : '') +
                                 (selectedReport?.exportExcel ? 'Excel; ' : '') +
                                 (selectedReport?.exportPDF ? 'PDF; ' : '') +
                                 (selectedReport?.useDatasetInputs ? 'DS Inputs; ' : '');

        formRetainBIMaintenance.setFieldValue('biPurgeReportAttributes', selectedAttributes);

        setIsTargetPurgeButtonDisabled(false);
    }

    const handleReportDataSetInputScanWorkflowEnded = (jobId: string, terminalStatus: string) => {
        if (terminalStatus !== 'finished') {
            return;
        }

        getBIReportDataSetInputScanSummary({ jobId: jobId });
    };

    const buildReportDataSetInputScanRequest = () => {
        const values = formRetainBIMaintenance.getFieldsValue();

        return {
            scanMode: values.biReportDataSetInputScanMode ?? 'all-reports',
            reportId: values.biReportDataSetInputReportID ?? null,
            inputModeFilter: values.biReportDataSetInputModeFilter ?? 'all',
            outputFocus: values.biReportDataSetInputOutputFocus ?? 'exceptions-only'
        };
    };

    const handleReportDataSetInputScanSelectionChange = () => {
        const values = formRetainBIMaintenance.getFieldsValue();

        const scanMode = values.biReportDataSetInputScanMode;
        let reportId = values.biReportDataSetInputReportID;

        if (scanMode !== 'single-report' && reportId) {
            formRetainBIMaintenance.setFieldValue('biReportDataSetInputReportID', null);
            reportId = null;
        }

        const requiresReportId = scanMode === 'single-report';
        const hasReportId = !!reportId && reportId.toString().trim().length > 0;

        setIsReportDataSetInputScanButtonDisabled(requiresReportId && !hasReportId);
    };

    const handleRecordCleanupLocatorSelectionChange = () => {
        const values = formRetainBIMaintenance.getFieldsValue();

        const hasSelectedScan =
            values.biRecordCleanupTenantScan ||
            values.biRecordCleanupReportScan ||
            values.biRecordCleanupFTPServerScan ||
            values.biRecordCleanupDataSetScan ||
            values.biRecordCleanupChartScan ||
            values.biRecordCleanupIncompleteReportItemScan;

        setIsRecordCleanupLocatorButtonDisabled(!hasSelectedScan);
    };

    const buildRecordCleanupLocatorTargets = () => {
        const values = formRetainBIMaintenance.getFieldsValue();

        const targets: {
            parentSchema: string;
            parentTable: string;
        }[] = [];

        if (values.biRecordCleanupTenantScan) {
            targets.push({
                parentSchema: 'dbo',
                parentTable: 'Tenant',
            });
        }

        if (values.biRecordCleanupReportScan) {
            targets.push({
                parentSchema: 'dbo',
                parentTable: 'Report',
            });
        }

        if (values.biRecordCleanupFTPServerScan) {
            targets.push({
                parentSchema: 'dbo',
                parentTable: 'FTP_Server',
            });
        }

        if (values.biRecordCleanupDataSetScan) {
            targets.push({
                parentSchema: 'dbo',
                parentTable: 'DataSet',
            });
        }

        if (values.biRecordCleanupChartScan) {
            targets.push({
                parentSchema: 'dbo',
                parentTable: 'Chart',
            });
        }

        return targets;
    };

    const buildRecordCleanupLocatorIncompleteChecks = () => {
        const values = formRetainBIMaintenance.getFieldsValue();

        const incompleteChecks: {
            checkType: string;
        }[] = [];

        if (values.biRecordCleanupIncompleteReportItemScan) {
            incompleteChecks.push({
                checkType: 'IncompleteReportItem',
            });
        }

        return incompleteChecks;
    };

    const handleRecordCleanupLocatorWorkflowEnded = (jobId: string, terminalStatus: string) => {
        if (terminalStatus === 'finished') {
            formRetainBIMaintenance.setFieldsValue({
                biRecordCleanupTenantScan: false,
                biRecordCleanupReportScan: false,
                biRecordCleanupFTPServerScan: false,
                biRecordCleanupDataSetScan: false,
                biRecordCleanupChartScan: false,
                biRecordCleanupIncompleteReportItemScan: false
            });

            setIsRecordCleanupLocatorButtonDisabled(true);

            getBIRecordCleanupLocatorSummary({ jobId: jobId });
        }
    };

    const buildWorkflowHistoryUrl = (jobId: string) => {
        return `${process.env.REACT_APP_API_ROOT}/api/workflows/` + jobId + `/history`;
    };

    const buildWorkflowEventUrl = (jobId: string) => {
        return `${process.env.REACT_APP_API_ROOT}/api/workflows/` + jobId + `/events`;
    };

    const renderWorkflowStep = (step: MultiStepEvent) => {
        return (
            <div
                style={{
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    width: '100%',
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: 10,
                    backgroundColor:
                        step.status === 'failed'
                        ? '#ffe6e6'
                        : step.status === 'finished'
                        ? '#e6ffe6'
                        : '#f9f9f9'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: '16px',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%'
                    }}
                >
                    <div>
                        <strong>Status:</strong> {step.status}
                    </div>
                    <div>
                        <strong>Progress:</strong> {step.percent ?? 0}%
                    </div>
                </div>

                <div>
                    {step.message}
                </div>

                {
                    step.errorCode && (
                        <div style={{ color: 'red' }}>
                            <strong>Error:</strong> {step.errorCode}
                        </div>
                    )
                }

                {
                    step.errorDetails && (
                        <div
                            style={{
                                color: 'red',
                                marginLeft: '24px',
                                marginRight: '24px',
                                maxHeight: '40px',
                                maxWidth: '100%',
                                minWidth: 0,
                                overflowX: 'hidden',
                                overflowY: 'auto',
                                overflowWrap: 'anywhere',
                                paddingLeft: '12px',
                                paddingRight: '12px',
                                textAlign: 'left',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}
                        >
                            <strong>Details:</strong> {step.errorDetails}
                        </div>
                    )
                }
            </div>
        );
    };

    //=================================================================
    //=================================================================

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        margin: '8px auto 0 auto',
        maxWidth: '760px',
        minHeight: '640px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
    };

    const reportDataSetInputScanLabelCol = {
        flex: '120px'
    };

    const tabContentLayoutStyle: React.CSSProperties = {
        alignItems: 'flex-start',
        display: 'flex',
        gap: '16px',
        justifyContent: 'flex-start',
        overflowX: 'auto',
        width: '100%'
    };

    const leftPanelStyle: React.CSSProperties = {
        flex: '0 0 760px',
        maxWidth: '760px',
        minWidth: '760px'
    };

    const resultsPanelStyle: React.CSSProperties = {
        backgroundColor: 'white',
        border: `1px solid black`,
        borderRadius: '10px',
        color: 'black',
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        margin: '8px 0 0 0',
        height: '640px',
        minWidth: '360px',
        overflowX: 'hidden',
        overflowY: 'auto',
        padding: '12px',
        textAlign: 'left'
    };

    const resultsSummaryAreaStyle: React.CSSProperties = {
        minHeight: '140px'
    };

    const renderTargetPurgeTab = () => {
        return (
            <div style={tabContentLayoutStyle}>
                <div style={leftPanelStyle}>
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22} style={{ textAlign: 'center' }}>
                                <Text underline style={{fontSize: '24px', fontWeight: 'bold'}}>
                                    BI Target Purge Process
                                </Text>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={4} />
                            <Col span={16}>
                                <Form.Item
                                    key='bipurgetargettype'
                                    name='biPurgeTargetType'
                                    initialValue='report'
                                    style={{ paddingTop: '4px', marginBottom: '0px' }}
                                >
                                    <Radio.Group
                                        buttonStyle='solid'
                                        value={biPurgeTargetType}
                                        onChange={handleBIPurgeTargetTypeChange}
                                    >
                                        <Radio.Button value='report'>Report</Radio.Button>
                                        <Radio.Button value='chart'>Chart</Radio.Button>
                                        <Radio.Button value='dataset'>DataSet</Radio.Button>
                                        <Radio.Button value='tenant'>Tenant</Radio.Button>
                                        <Radio.Button value='ftpserver'>FTP Server</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col span={4} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={8}>
                                <Text italic style={{float: 'left', fontWeight: 'bold'}}>
                                    Select a {biPurgeTargetDisplayName} to begin:
                                </Text>
                            </Col>
                            <Col span={15} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Select
                                    filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                    key={`bipurgetargetselect-${biPurgeTargetType}`}
                                    placeholder={`Select a ${biPurgeTargetDisplayName}`}
                                    showSearch
                                    styles={{ popup: { root: { width: 500 } } }}
                                    style={{width: '100%'}}
                                    value={biSelectedTargetID ? Number(biSelectedTargetID) : undefined}
                                    onChange={(option: number) => handleBIPurgeTargetSelectionChange(option)}
                                >
                                    {renderBIPurgeTargetOptions()}
                                </Select>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={7}>
                                <Text italic style={reportOnlyTextStyle}>Associated Tenant:</Text>
                            </Col>
                            <Col span={16} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Form.Item
                                    key='bipurgetenantname'
                                    name='biPurgeTenantName'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input readOnly disabled={!isBIPurgeReportTarget} />
                                </Form.Item>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={9} style={{paddingTop: '8px', textAlign: 'right', verticalAlign: 'middle'}}>
                                {biPurgeTargetDisplayName} ID:
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    key='bipurgetargetid'
                                    name='biPurgeTargetID'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input readOnly />
                                </Form.Item>
                            </Col>
                            <Col span={9} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={5} style={reportOnlyLabelStyle}>
                                Report Group Name:
                            </Col>
                            <Col span={7}>
                                <Form.Item
                                    key='bipurgereportgroupname'
                                    name='biPurgeReportGroupName'
                                    style={{marginBottom: '4px', width: '95%'}}
                                >
                                    <Input readOnly disabled={!isBIPurgeReportTarget} />
                                </Form.Item>
                            </Col>
                            <Col span={4} style={{paddingTop: '8px', textAlign: 'right', verticalAlign: 'middle'}}>
                                {biPurgeTargetDisplayName} Name:
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='bipurgetargetname'
                                    name='biPurgeTargetName'
                                    style={{marginBottom: '4px', width: '95%'}}
                                >
                                    <Input
                                        readOnly
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={24} style={{ textAlign: 'center' }}>
                                <Form.Item
                                    key='bitargetpurgepreviewonly'
                                    name='biTargetPurgePreviewOnly'
                                    valuePropName='checked'
                                    initialValue={true}
                                    style={{ marginBottom: '2px', marginTop: '2px' }}
                                >
                                    <Checkbox
                                        style={{ fontWeight: 'bold' }}
                                        onChange={(e) => setIsTargetPurgePreviewOnly(e.target.checked)}
                                    >
                                        Generate Delete Statements ONLY. (WILL NOT EXECUTE.)
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                        <div style={{ flex: 1 }} />
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <MultiStepJobViewer
                                    title=''
                                    startButtonText={
                                        isTargetPurgePreviewOnly
                                            ? 'Generate Delete Statements'
                                            : 'Start Target Purge'
                                    }
                                    startButtonDisabled={isTargetPurgeButtonDisabled}
                                    startConfirmationTitle={
                                        isTargetPurgePreviewOnly
                                            ? 'Generate Delete Statements Confirmation'
                                            : 'Target Purge Confirmation'
                                    }
                                    startConfirmationMessage={
                                        isTargetPurgePreviewOnly
                                            ? (
                                                <div>
                                                    <b>
                                                        This Action Will Generate Delete Statements For {biPurgeTargetDisplayName}:
                                                    </b>
                                                    <br />
                                                    <b>
                                                        {
                                                            isBIPurgeReportTarget
                                                                ? ' [' + biSelectedTargetGroupName + ']:' + biSelectedTargetName
                                                                : biSelectedTargetName
                                                        }
                                                    </b>
                                                    <br />
                                                    For <u>All</u> Data Related To The Selected Target.
                                                    <br />
                                                    <br />
                                                    <b>
                                                        NO Records Will Be Deleted.
                                                    </b>
                                                </div>
                                            )
                                            : (
                                                <div>
                                                    <b>
                                                        This Action Will <u>Permanently</u> Purge {biPurgeTargetDisplayName}:
                                                    </b>
                                                    <br />
                                                    <b>
                                                        {
                                                            isBIPurgeReportTarget
                                                                ? ' [' + biSelectedTargetGroupName + ']:' + biSelectedTargetName
                                                                : biSelectedTargetName
                                                        }
                                                    </b>
                                                    <br />
                                                    And <u>All</u> Data Related To The Selected Target.
                                                    <br />
                                                    <br />
                                                    <b>
                                                        This Action <u>Cannot</u> Be Undone.
                                                    </b>
                                                </div>
                                            )
                                    }
                                    stepsTitle='Not Running'

                                    startUrl={`${process.env.REACT_APP_API_ROOT}/api/workflows`}

                                    buildStartBody={
                                        function () {
                                            setTargetPurgeSummary(null);

                                            const targetPurgeRequest: BITargetPurgeRequestModel = {
                                                userID: user?.id ?? 0,
                                                tenantID: biSelectedTenantID,
                                                purgeTargetType: biPurgeTargetType,
                                                targetID: Number(biSelectedTargetID),
                                                targetGroupName: biSelectedTargetGroupName,
                                                targetName: biSelectedTargetName,
                                                previewOnly: isTargetPurgePreviewOnly
                                            };

                                            return {
                                                WorkflowType: 'reporting_target_purge_request',
                                                InputValueString: JSON.stringify(targetPurgeRequest),
                                                InputValueInteger: Number(biSelectedTargetID)
                                            };
                                        }
                                    }

                                    historyUrlBuilder={buildWorkflowHistoryUrl}

                                    eventUrlBuilder={buildWorkflowEventUrl}

                                    onWorkflowEnded={
                                        function (jobId, terminalStatus) {
                                            if (terminalStatus === 'finished') {
                                                if (isTargetPurgePreviewOnly) {
                                                    setIsTargetPurgeButtonDisabled(!biSelectedTargetID);
                                                }
                                                else {
                                                    setIsTargetPurgeButtonDisabled(true);
                                                    clearBIPurgeTargetSelection();
                                                    refetchBIPurgeTargetLists();
                                                }
                                            }

                                            getBITargetPurgeSummary({ jobId: jobId });
                                        }
                                    }

                                    renderStep={renderWorkflowStep}
                                />
                            </Col>
                            <Col span={1} />
                        </Row>
                    </div>
                </div>
                <div style={resultsPanelStyle}>
                    <Text style={{fontSize: '18px', fontWeight: 'bold'}}>
                        {
                            targetPurgeSummary?.finalizeMessage?.toUpperCase().includes('PREVIEW ONLY')
                                ? 'Generated Delete Statements'
                                : 'Target Purge Results'
                        }
                    </Text>
                    {
                        targetPurgeSummary
                        ?
                        (
                            <div style={{display: 'flex', flex: 1, flexDirection: 'column', marginTop: '12px', minHeight: 0}}>
                            {
                                (() => {
                                    const targetPurgeDisplayStatus = targetPurgeSummary.previewOnly
                                        ? 'Preview Only'
                                        : targetPurgeSummary.rolledBack
                                            ? 'Rolled Back'
                                            : targetPurgeSummary.succeeded
                                                ? 'Completed'
                                                : targetPurgeSummary.error
                                                    ? 'Failed'
                                                    : 'In Progress';

                                    const deleteStatementSections = [
                                        {
                                            key: 'chart',
                                            label: 'Chart',
                                            statements: targetPurgeSummary.chartDeleteStatements ?? []
                                        },
                                        {
                                            key: 'dataset',
                                            label: 'DataSet',
                                            statements: targetPurgeSummary.datasetDeleteStatements ?? []
                                        },
                                        {
                                            key: 'report',
                                            label: 'Report',
                                            statements: targetPurgeSummary.reportDeleteStatements ?? []
                                        },
                                        {
                                            key: 'ftpserver',
                                            label: 'FTP Server',
                                            statements: targetPurgeSummary.ftpServerDeleteStatements ?? []
                                        },
                                        {
                                            key: 'tenant',
                                            label: 'Tenant',
                                            statements: targetPurgeSummary.tenantDeleteStatements ?? []
                                        }
                                    ].filter((section) => section.statements.length > 0);

                                    return (
                                        <>
                                            <div style={resultsSummaryAreaStyle}>
                                                <div>
                                                    <Text>Status: {targetPurgeDisplayStatus}</Text>
                                                </div>
                                                <div>
                                                    <Text>Target Type: {targetPurgeSummary.purgeTargetType}</Text>
                                                    <Text>Target ID: {targetPurgeSummary.targetId}</Text>
                                                </div>
                                                <div>
                                                    <Text>Total Delete Statements: {targetPurgeSummary.totalDeleteStatements}</Text>
                                                </div>
                                                <div>
                                                    <Text>Outcome: {targetPurgeSummary.finalizeMessage}</Text>
                                                </div>
                                                {
                                                    targetPurgeSummary.failedStepName &&
                                                    (
                                                        <div>
                                                            <Text>Failed Step: {targetPurgeSummary.failedStepName}</Text>
                                                        </div>
                                                    )
                                                }
                                                {
                                                    targetPurgeSummary.failedStatement &&
                                                    (
                                                        <div>
                                                            <Text>Failed Statement: {targetPurgeSummary.failedStatement}</Text>
                                                        </div>
                                                    )
                                                }
                                                {
                                                    targetPurgeSummary.error &&
                                                    (
                                                        <div>
                                                            <Text type='danger'>Error: {targetPurgeSummary.error}</Text>
                                                        </div>
                                                    )
                                                }
                                                {
                                                    (targetPurgeSummary.warningMessages?.length ?? 0) > 0 &&
                                                    (
                                                        <div>
                                                            <Text type='warning'>Warnings: {targetPurgeSummary.warningMessages.join(' ')}</Text>
                                                        </div>
                                                    )
                                                }
                                                {
                                                    (targetPurgeSummary.infoMessages?.length ?? 0) > 0 &&
                                                    (
                                                        <div>
                                                            <Text>Info: {targetPurgeSummary.infoMessages.join(' ')}</Text>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                            {
                                                deleteStatementSections.length > 0 &&
                                                (
                                                    <div
                                                        style={{
                                                            flex: 1,
                                                            marginTop: '16px',
                                                            minHeight: 0,
                                                            overflowY: 'auto',
                                                            paddingRight: '8px'
                                                        }}
                                                    >
                                                        {
                                                            deleteStatementSections.map((section) => (
                                                                <Collapse
                                                                    key={section.key}
                                                                    size='small'
                                                                    style={{marginBottom: '12px'}}
                                                                    items={[{
                                                                        key: section.key,
                                                                        label: (
                                                                            <div
                                                                                style={{
                                                                                    alignItems: 'center',
                                                                                    display: 'flex',
                                                                                    justifyContent: 'space-between',
                                                                                    width: '100%'
                                                                                }}
                                                                            >
                                                                                <Text style={{fontWeight: 'bold'}}>
                                                                                    {section.label}
                                                                                </Text>
                                                                                <Text>
                                                                                    {section.statements.length} statement{section.statements.length === 1 ? '' : 's'}
                                                                                </Text>
                                                                            </div>
                                                                        ),
                                                                        children: (
                                                                            <Table
                                                                                className='maintenance-results-table'
                                                                                columns={[
                                                                                    {
                                                                                        title: 'Delete Statement',
                                                                                        dataIndex: 'statement',
                                                                                        key: 'statement'
                                                                                    }
                                                                                ]}
                                                                                dataSource={section.statements.map((statement, index) => ({
                                                                                    key: `${section.key}-${index}`,
                                                                                    statement: statement
                                                                                }))}
                                                                                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                                                                                rowKey='key'
                                                                                scroll={{ x: 750 }}
                                                                                size='small'
                                                                            />
                                                                        )
                                                                    }]}
                                                                />
                                                            ))
                                                        }
                                                    </div>
                                                )
                                            }
                                        </>
                                    );
                                })()
                            }
                            </div>
                        )
                        :
                        (
                            <div style={{marginTop: '12px'}}>
                                <Text>
                                    No selected target purge results available.
                                </Text>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    };

    const renderRecordCleanupTab = () => {
        return (
            <div style={tabContentLayoutStyle}>
                <div style={leftPanelStyle}>
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22} style={{ textAlign: 'center' }}>
                                <Text underline style={{fontSize: '24px', fontWeight: 'bold'}}>
                                    Record Cleanup Locator
                                </Text>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: 'blue', margin: '2px' }}>
                                    Unused Records Locator
                                </Divider>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={8}>
                                <Form.Item
                                    key='birecordcleanuptenantscan'
                                    name='biRecordCleanupTenantScan'
                                    valuePropName='checked'
                                    style={{marginBottom: '0px', marginTop: '0px'}}
                                >
                                    <Checkbox
                                        style={{float: 'left'}}
                                        onChange={handleRecordCleanupLocatorSelectionChange}                                                >
                                        Scan Tenants
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={1} />
                            <Col span={8}>
                                <Form.Item
                                    key='birecordcleanupreportscan'
                                    name='biRecordCleanupReportScan'
                                    valuePropName='checked'
                                    style={{marginBottom: '0px', marginTop: '0px'}}
                                >
                                    <Checkbox
                                        style={{float: 'left'}}
                                        onChange={handleRecordCleanupLocatorSelectionChange}                                                >
                                        Scan Reports
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={6} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid lightgray', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={8}>
                                <Form.Item
                                    key='birecordcleanupdatasetscan'
                                    name='biRecordCleanupDataSetScan'
                                    valuePropName='checked'
                                    style={{marginBottom: '0px', marginTop: '0px'}}
                                >
                                    <Checkbox
                                        style={{float: 'left'}}
                                        onChange={handleRecordCleanupLocatorSelectionChange}                                                >
                                        Scan DataSets
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={1} />
                            <Col span={8}>
                                <Form.Item
                                    key='birecordcleanupchartscan'
                                    name='biRecordCleanupChartScan'
                                    valuePropName='checked'
                                    style={{marginBottom: '0px', marginTop: '0px'}}
                                >
                                    <Checkbox
                                        style={{float: 'left'}}
                                        onChange={handleRecordCleanupLocatorSelectionChange}
                                    >
                                        Scan Charts
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={6} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid lightgray', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={8}>
                                <Form.Item
                                    key='birecordcleanupftpserverscan'
                                    name='biRecordCleanupFTPServerScan'
                                    valuePropName='checked'
                                    style={{marginBottom: '0px', marginTop: '0px'}}
                                >
                                    <Checkbox
                                        style={{float: 'left'}}
                                        onChange={handleRecordCleanupLocatorSelectionChange}
                                    >
                                        Scan FTP Servers
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={1} />
                            <Col span={8}>
                            </Col>
                            <Col span={6} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid lightgray', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: 'blue', margin: '2px' }}>
                                    Incomplete Records Locator
                                </Divider>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={12}>
                                <Form.Item
                                    key='birecordcleanupincompletereportitemscan'
                                    name='biRecordCleanupIncompleteReportItemScan'
                                    valuePropName='checked'
                                    style={{marginBottom: '0px', marginTop: '0px'}}
                                >
                                    <Checkbox
                                        style={{float: 'left'}}
                                        onChange={handleRecordCleanupLocatorSelectionChange}
                                    >
                                        Scan Report Items (Missing Chart And DataSet)
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={11} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid lightgray', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <div style={{ flex: 1 }} />
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <MultiStepJobViewer
                                    title=''
                                    startButtonText='Start Record Cleanup Scan'
                                    startButtonDisabled={isRecordCleanupLocatorButtonDisabled}
                                    startConfirmationTitle='Record Cleanup Confirmation'
                                    startConfirmationMessage={
                                        <div>
                                            <b>
                                                This Action Will Scan Selected Targets For Record Cleanup Candidates.
                                            </b>
                                            <br />
                                            <br />
                                            Record Cleanup Candidates Will Be Reported Only.
                                            <br />
                                            <b>
                                                No Records Will Be Deleted.
                                            </b>
                                        </div>
                                    }
                                    stepsTitle='Not Running'

                                    startUrl={`${process.env.REACT_APP_API_ROOT}/api/workflows`}

                                    buildStartBody={
                                        function () {
                                            setRecordCleanupLocatorSummary(null);

                                            const targets = buildRecordCleanupLocatorTargets();
                                            const incompleteChecks = buildRecordCleanupLocatorIncompleteChecks();

                                            return {
                                                WorkflowType: 'reporting_record_cleanup_locator_request',
                                                InputValueString: JSON.stringify({
                                                    userID: user?.id ?? 0,
                                                    targets: targets,
                                                    incompleteChecks: incompleteChecks
                                                }),
                                                InputValueInteger: 0
                                            };
                                        }
                                    }

                                    historyUrlBuilder={buildWorkflowHistoryUrl}

                                    eventUrlBuilder={buildWorkflowEventUrl}

                                    onWorkflowEnded={handleRecordCleanupLocatorWorkflowEnded}

                                    renderStep={renderWorkflowStep}
                                />
                            </Col>
                            <Col span={1} />
                        </Row>
                    </div>
                </div>
                <div style={resultsPanelStyle}>
                    <Text style={{fontSize: '18px', fontWeight: 'bold'}}>
                        Record Cleanup Results
                    </Text>
                    {
                        recordCleanupLocatorSummary
                        ?
                        (
                            <div style={{display: 'flex', flex: 1, flexDirection: 'column', marginTop: '12px', minHeight: 0}}>
                                {
                                    (() => {
                                        const recordCleanupSections = Object.entries(
                                            recordCleanupLocatorSummary.records.reduce<Record<string, typeof recordCleanupLocatorSummary.records>>(
                                                (currentSections, record) => {
                                                    const parentTable = record.parentTable || 'Other';

                                                    if (!currentSections[parentTable]) {
                                                        currentSections[parentTable] = [];
                                                    }

                                                    currentSections[parentTable].push(record);

                                                    return currentSections;
                                                },
                                                {}
                                            )
                                        ).map(([parentTable, records]) => ({
                                            parentTable,
                                            records
                                        }));

                                        return (
                                            <>
                                                <div style={{marginBottom: '8px'}}>
                                                    <div>
                                                        <Text>Total Targets Requested: {recordCleanupLocatorSummary.totalTargetsRequested}</Text>
                                                    </div>
                                                    <div>
                                                        <Text>Total Targets Processed: {recordCleanupLocatorSummary.totalTargetsProcessed}</Text>
                                                    </div>
                                                    <div>
                                                        <Text>Total Record Cleanups Found: {recordCleanupLocatorSummary.totalRecordCleanupsFound}</Text>
                                                    </div>
                                                </div>
                                                {
                                                    recordCleanupSections.length > 0 &&
                                                    (
                                                        <div
                                                            style={{
                                                                flex: 1,
                                                                marginTop: '8px',
                                                                minHeight: 0,
                                                                overflowY: 'auto',
                                                                paddingRight: '8px'
                                                            }}
                                                        >
                                                            {
                                                                recordCleanupSections.map((section) => {
                                                                    const unusedCount = section.records.filter((record) => record.cleanupCategory === 'Unused').length;
                                                                    const incompleteCount = section.records.filter((record) => record.cleanupCategory === 'Incomplete').length;

                                                                    return (
                                                                        <Collapse
                                                                            key={section.parentTable}
                                                                            size='small'
                                                                            style={{marginBottom: '12px'}}
                                                                            items={[{
                                                                                key: section.parentTable,
                                                                                label: (
                                                                                    <div
                                                                                        style={{
                                                                                            alignItems: 'center',
                                                                                            display: 'flex',
                                                                                            justifyContent: 'space-between',
                                                                                            width: '100%'
                                                                                        }}
                                                                                    >
                                                                                        <Text style={{fontWeight: 'bold'}}>
                                                                                            {section.parentTable}
                                                                                        </Text>
                                                                                        <Space size='small'>
                                                                                            <Text>
                                                                                                {section.records.length} record{section.records.length === 1 ? '' : 's'}
                                                                                            </Text>
                                                                                            {
                                                                                                unusedCount > 0 && (
                                                                                                    <Tag color='blue'>
                                                                                                        {unusedCount} Unused
                                                                                                    </Tag>
                                                                                                )
                                                                                            }
                                                                                            {
                                                                                                incompleteCount > 0 && (
                                                                                                    <Tag color='gold'>
                                                                                                        {incompleteCount} Incomplete
                                                                                                    </Tag>
                                                                                                )
                                                                                            }
                                                                                        </Space>
                                                                                    </div>
                                                                                ),
                                                                                children: (
                                                                                    <Table
                                                                                        className='maintenance-results-table'
                                                                                        columns={[
                                                                                            {
                                                                                                title: 'Parent Type',
                                                                                                dataIndex: 'parentTable',
                                                                                                key: 'parentTable',
                                                                                                width: 120
                                                                                            },
                                                                                            {
                                                                                                title: 'Parent ID',
                                                                                                dataIndex: 'parentKeyValue',
                                                                                                key: 'parentKeyValue',
                                                                                                width: 120
                                                                                            },
                                                                                            {
                                                                                                title: 'Identifier',
                                                                                                dataIndex: 'parentNameValue',
                                                                                                key: 'parentNameValue',
                                                                                                render: (value: string | null) => value && value.trim() !== '' ? value : 'N/A'
                                                                                            },
                                                                                            {
                                                                                                title: 'Category',
                                                                                                dataIndex: 'cleanupCategory',
                                                                                                key: 'cleanupCategory',
                                                                                                width: 120
                                                                                            },
                                                                                            {
                                                                                                title: 'Cleanup Type',
                                                                                                dataIndex: 'cleanupType',
                                                                                                key: 'cleanupType'
                                                                                            },
                                                                                            {
                                                                                                title: 'Key Column',
                                                                                                dataIndex: 'parentKeyColumn',
                                                                                                key: 'parentKeyColumn'
                                                                                            }
                                                                                        ]}
                                                                                        dataSource={section.records.map((record, index) => ({
                                                                                            ...record,
                                                                                            key: `${section.parentTable}-${index}`
                                                                                        }))}
                                                                                        pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                                                                                        rowKey='key'
                                                                                        scroll={{ x: 1100 }}
                                                                                        size='small'
                                                                                    />
                                                                                )
                                                                            }]}
                                                                        />
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    )
                                                }
                                            </>
                                        );
                                    })()
                                }
                            </div>
                        )
                        :
                        (
                            <div style={{marginTop: '12px'}}>
                                <Text>
                                    No record cleanup results available.
                                </Text>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    };

    const renderReportDataSetInputScanningTab = () => {
        return (
            <div style={tabContentLayoutStyle}>
                <div style={leftPanelStyle}>
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22} style={{ textAlign: 'center' }}>
                                <Text underline style={{fontSize: '24px', fontWeight: 'bold'}}>
                                    Report / DataSet Input Scanning
                                </Text>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Form.Item
                                    initialValue='all-reports'
                                    label='Scan Mode'
                                    labelCol={reportDataSetInputScanLabelCol}
                                    name='biReportDataSetInputScanMode'
                                    style={{ marginBottom: '8px' }}
                                >
                                    <Select onChange={handleReportDataSetInputScanSelectionChange}>
                                        <Option value='single-report'>Single Report</Option>
                                        <Option value='all-reports'>All Reports</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={1} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Form.Item
                                    extra='Used only when Scan Mode is Single Report.'
                                    label='Report'
                                    labelCol={reportDataSetInputScanLabelCol}
                                    name='biReportDataSetInputReportID'
                                    style={{ marginBottom: '8px' }}
                                >
                                    <Select
                                        showSearch
                                        optionFilterProp='label'
                                        onChange={handleReportDataSetInputScanSelectionChange}
                                        disabled={biReportDataSetInputScanMode !== 'single-report'}
                                        options={
                                            reports.map(
                                                (report) => ({
                                                    value: report.reportId,
                                                    label: `[${report.reportGroupName}] ${report.reportName}`
                                                })
                                            )
                                        }
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Form.Item
                                    initialValue='all'
                                    label='Input Mode Filter'
                                    labelCol={reportDataSetInputScanLabelCol}
                                    name='biReportDataSetInputModeFilter'
                                    style={{ marginBottom: '8px' }}
                                >
                                    <Select>
                                        <Option value='all'>All</Option>
                                        <Option value='dataset-inputs'>Use_Dataset_Inputs = 1</Option>
                                        <Option value='report-inputs'>Use_Dataset_Inputs = 0</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Form.Item
                                    initialValue='exceptions-only'
                                    label='Output Focus'
                                    labelCol={reportDataSetInputScanLabelCol}
                                    name='biReportDataSetInputOutputFocus'
                                    style={{ marginBottom: '8px' }}
                                >
                                    <Select>
                                        <Option value='all-findings'>All Findings</Option>
                                        <Option value='exceptions-only'>Exceptions Only</Option>
                                        <Option value='clean-reports-only'>Clean Reports Only</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <div style={{ flex: 1 }} />
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <MultiStepJobViewer
                                    title=''
                                    startButtonText='Start Scan'
                                    startButtonDisabled={isReportDataSetInputScanButtonDisabled}
                                    startConfirmationTitle='Report / DataSet Input Scan Confirmation'
                                    startConfirmationMessage={
                                        <div>
                                            <b>
                                                This action will scan report / dataset input configuration.
                                            </b>
                                            <br />
                                            <br />
                                            <b>
                                                Scan Mode:
                                            </b>
                                            {' '}
                                            {biReportDataSetInputScanMode === 'single-report'
                                                ? 'Single Report'
                                                : 'All Reports'}
                                            <br />
                                            {
                                                biReportDataSetInputScanMode === 'single-report' &&
                                                (
                                                    <>
                                                        <b>
                                                            Report ID:
                                                        </b>
                                                        {' '}
                                                        {biReportDataSetInputReportID || '(none)'}
                                                        <br />
                                                    </>
                                                )
                                            }
                                            <br />
                                            <b>
                                                No data will be changed.
                                            </b>
                                        </div>
                                    }
                                    stepsTitle='Not Running'
                                    startUrl={`${process.env.REACT_APP_API_ROOT}/api/workflows`}
                                    buildStartBody={
                                        function () {
                                            setReportDataSetInputScanSummary(null);

                                            return {
                                                WorkflowType: 'report_dataset_input_scanning_request',
                                                InputValueString: JSON.stringify({
                                                    userID: user?.id ?? 0,
                                                    ...buildReportDataSetInputScanRequest()
                                                }),
                                                InputValueInteger: 0
                                            };
                                        }
                                    }

                                    historyUrlBuilder={buildWorkflowHistoryUrl}

                                    eventUrlBuilder={buildWorkflowEventUrl}

                                    onWorkflowEnded={handleReportDataSetInputScanWorkflowEnded}

                                    renderStep={renderWorkflowStep}
                                />
                            </Col>
                            <Col span={1} />
                        </Row>
                    </div>
                </div>
                <div style={resultsPanelStyle}>
                    <Text style={{fontSize: '18px', fontWeight: 'bold'}}>
                        Report / DataSet Input Scan Results
                    </Text>
                    {
                        reportDataSetInputScanSummary
                        ?
                        (
                            <div style={{marginTop: '12px'}}>
                                <div style={{marginBottom: '8px'}}>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))',
                                            gap: '4px 16px'
                                        }}
                                    >
                                        <Text>Total Reports Scanned: {reportDataSetInputScanSummary.totalReportsScanned}</Text>
                                        <Text>Total Findings: {reportDataSetInputScanSummary.totalFindings}</Text>
                                    </div>
                                </div>
                                {
                                    reportDataSetInputScanSummary.results.length > 0 &&
                                    (
                                        <div style={{marginTop: '8px'}}>
                                            <Table
                                                className='maintenance-results-table'
                                                columns={[
                                                    {
                                                        title: 'Report ID',
                                                        dataIndex: 'reportID',
                                                        key: 'reportID'
                                                    },
                                                    {
                                                        title: 'Group Name',
                                                        dataIndex: 'groupName',
                                                        key: 'groupName'
                                                    },
                                                    {
                                                        title: 'Report Name',
                                                        dataIndex: 'reportName',
                                                        key: 'reportName'
                                                    },
                                                    {
                                                        title: 'Use DS Inputs',
                                                        dataIndex: 'useDatasetInputs',
                                                        key: 'useDatasetInputs',
                                                        render: (value: boolean) => value ? 'Yes' : 'No'
                                                    },
                                                    {
                                                        title: 'Finding Type',
                                                        dataIndex: 'findingType',
                                                        key: 'findingType'
                                                    },
                                                    {
                                                        title: 'Details',
                                                        dataIndex: 'details',
                                                        key: 'details'
                                                    }
                                                ]}
                                                dataSource={reportDataSetInputScanSummary.results.map((resultRow, index) => ({
                                                    ...resultRow,
                                                    key: index.toString()
                                                }))}
                                                rowKey='key'
                                                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                                                scroll={{ x: 1100, y: 420 }}
                                                size='small'
                                            />
                                        </div>
                                    )
                                }
                                {
                                    reportDataSetInputScanSummary.results.length === 0 &&
                                    (
                                        <div style={{marginTop: '16px'}}>
                                            <Text>
                                                Scan completed. No findings were returned.
                                            </Text>
                                        </div>
                                    )
                                }
                            </div>
                        )
                        :
                        (
                            <div style={{marginTop: '12px'}}>
                                <Text>
                                    No report / dataset input scan results available.
                                </Text>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    };

    const renderReportLinkageTraceTab = () => {
        const selectedReport = reports.find((report) => report.reportId === Number(biSelectedReportID));
        const groupedTraceRows = groupReportLinkageTraceRowsBySection();
        const warningCount = reportLinkageTraceRows.filter((traceRow) => traceRow.warningText && traceRow.warningText.trim() !== '').length;
        const missingCount = reportLinkageTraceRows.filter((traceRow) => traceRow.isMissing).length;
        const deletedCount = reportLinkageTraceRows.filter((traceRow) => traceRow.isDeleted).length;

        return (
            <div style={tabContentLayoutStyle}>
                <div style={leftPanelStyle}>
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22} style={{ textAlign: 'center' }}>
                                <Text underline style={{fontSize: '24px', fontWeight: 'bold'}}>
                                    Report Linkage Trace
                                </Text>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={8}>
                                <Text italic style={{float: 'left', fontWeight: 'bold'}}>
                                    Select a Report to begin:
                                </Text>
                            </Col>
                            <Col span={15} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Select
                                    filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                    key={`bireportlinkagetraceselect`}
                                    options={
                                        reports.map(
                                            (report) => ({
                                                value: report.reportId,
                                                label: `[${report.reportGroupName}] ${report.reportName}`
                                            })
                                        )
                                    }
                                    placeholder={`Select a Report`}
                                    showSearch
                                    styles={{ popup: { root: { width: 500 } } }}
                                    style={{width: '100%'}}
                                    value={biSelectedReportID ? Number(biSelectedReportID) : undefined}
                                    onChange={handleReportLinkageTraceSelectionChange}
                                />
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Divider style={{ borderTop: '1px solid black', margin: '1px' }} />
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={9} style={{paddingTop: '8px', textAlign: 'right', verticalAlign: 'middle'}}>
                                Report ID:
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    key='bireporttracelinkagereportid'
                                    name='biReportTraceLinkageReportId'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input readOnly />
                                </Form.Item>
                            </Col>
                            <Col span={9} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={5} style={reportOnlyLabelStyle}>
                                Report Group Name:
                            </Col>
                            <Col span={7}>
                                <Form.Item
                                    key='bireporttracelinkagereportgroupname'
                                    name='biReportTraceLinkageReportGroupName'
                                    style={{marginBottom: '4px', width: '95%'}}
                                >
                                    <Input readOnly />
                                </Form.Item>
                            </Col>
                            <Col span={4} style={{paddingTop: '8px', textAlign: 'right', verticalAlign: 'middle'}}>
                                Report Name:
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='bireporttracelinkagereportname'
                                    name='biReportTraceLinkageReportName'
                                    style={{marginBottom: '4px', width: '95%'}}
                                >
                                    <Input readOnly />
                                </Form.Item>
                            </Col>
                        </Row>
                        <div style={{ flex: 1 }} />
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Divider style={{ borderTop: '1px solid black', margin: '1px' }} />
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <MultiStepJobViewer
                                    title=''
                                    startButtonText='Trace Report Linkages'
                                    startButtonDisabled={isReportLinkageTraceButtonDisabled}
                                    startConfirmationTitle='Report Linkage Trace Confirmation'
                                    startConfirmationMessage={
                                        <div>
                                            <b>
                                                This action will trace report administrative and runtime linkages.
                                            </b>
                                            <br />
                                            <br />
                                            <b>
                                                Report ID:
                                            </b>
                                            {' '}
                                            {selectedReport?.reportId ?? '(none)'}
                                            <br />
                                            <b>
                                                Report:
                                            </b>
                                            {' '}
                                            {selectedReport ? `[${selectedReport.reportGroupName}] ${selectedReport.reportName}` : '(none)'}
                                            <br />
                                            <br />
                                            <b>
                                                No data will be changed.
                                            </b>
                                        </div>
                                    }
                                    stepsTitle='Not Running'
                                    startUrl={`${process.env.REACT_APP_API_ROOT}/api/workflows`}
                                    buildStartBody={
                                        function () {
                                            const reportID = Number(biSelectedReportID);

                                            setReportLinkageTraceRows([]);

                                            return {
                                                WorkflowType: 'reporting_report_linkage_trace_request',
                                                InputValueString: JSON.stringify({
                                                    userID: user?.id ?? 0,
                                                    reportID: reportID
                                                }),
                                                InputValueInteger: reportID
                                            };
                                        }
                                    }

                                    historyUrlBuilder={buildWorkflowHistoryUrl}

                                    eventUrlBuilder={buildWorkflowEventUrl}

                                    onWorkflowEnded={handleReportLinkageTraceWorkflowEnded}

                                    renderStep={renderWorkflowStep}
                                />
                            </Col>
                            <Col span={1} />
                        </Row>
                    </div>
                </div>
                <div style={resultsPanelStyle}>
                    <Text style={{fontSize: '18px', fontWeight: 'bold'}}>
                        Report Linkage Trace Results
                    </Text>
                    {
                        reportLinkageTraceRows.length > 0
                        ?
                        (
                            <div style={{display: 'flex', flex: 1, flexDirection: 'column', marginTop: '12px', minHeight: 0}}>
                                <div style={{marginBottom: '8px'}}>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))',
                                            gap: '4px 16px'
                                        }}
                                    >
                                        <Text>Total Rows: {reportLinkageTraceRows.length}</Text>
                                        <Text>Warnings: {warningCount}</Text>
                                        <Text>Missing: {missingCount}</Text>
                                        <Text>Deleted: {deletedCount}</Text>
                                    </div>
                                </div>
                                <div
                                    style={{
                                        marginTop: '16px',
                                        maxHeight: '560px',
                                        overflowY: 'auto',
                                        paddingRight: '8px'
                                    }}
                                >
                                    {
                                        groupedTraceRows.map((section) => (
                                            <Collapse
                                                key={section.sectionName}
                                                size='small'
                                                style={{marginBottom: '12px'}}
                                                items={[{
                                                    key: section.sectionName,
                                                    label: (
                                                        <div
                                                            style={{
                                                                alignItems: 'center',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                width: '100%'
                                                            }}
                                                        >
                                                            <Text style={{fontWeight: 'bold'}}>
                                                                {section.sectionName}
                                                            </Text>
                                                            <Text>
                                                                {section.rows.length} row{section.rows.length === 1 ? '' : 's'}
                                                            </Text>
                                                        </div>
                                                    ),
                                                    children: (
                                                        <>
                                                            <Table
                                                                className='maintenance-results-table'
                                                                columns={[
                                                                    {
                                                                        title: 'Entity Type',
                                                                        dataIndex: 'entityType',
                                                                        key: 'entityType',
                                                                        width: 160
                                                                    },
                                                                    {
                                                                        title: 'Entity ID',
                                                                        dataIndex: 'entityID',
                                                                        key: 'entityID',
                                                                        width: 110,
                                                                        render: (value: number | null) => value ?? 'N/A'
                                                                    },
                                                                    {
                                                                        title: 'Status',
                                                                        key: 'status',
                                                                        width: 170,
                                                                        filters: [
                                                                            { text: 'Missing', value: 'missing' },
                                                                            { text: 'Deleted', value: 'deleted' },
                                                                            { text: 'Warning', value: 'warning' },
                                                                            { text: 'OK', value: 'ok' }
                                                                        ],
                                                                        onFilter: (value, traceRow) => {
                                                                            if (value === 'missing') {
                                                                                return traceRow.isMissing;
                                                                            }

                                                                            if (value === 'deleted') {
                                                                                return traceRow.isDeleted;
                                                                            }

                                                                            if (value === 'warning') {
                                                                                return traceRow.warningText && traceRow.warningText.trim() !== '';
                                                                            }

                                                                            if (value === 'ok') {
                                                                                return !traceRow.isMissing &&
                                                                                    !traceRow.isDeleted &&
                                                                                    (!traceRow.warningText || traceRow.warningText.trim() === '');
                                                                            }

                                                                            return true;
                                                                        },
                                                                        render: (_: unknown, traceRow: GetReportLinkageTraceRowsModelResponse) => (
                                                                            <>
                                                                                {
                                                                                    traceRow.isMissing && (
                                                                                        <Tag color='red'>
                                                                                            Missing
                                                                                        </Tag>
                                                                                    )
                                                                                }
                                                                                {
                                                                                    traceRow.isDeleted && (
                                                                                        <Tag color='default'>
                                                                                            Deleted
                                                                                        </Tag>
                                                                                    )
                                                                                }
                                                                                {
                                                                                    traceRow.warningText && traceRow.warningText.trim() !== '' && (
                                                                                        <Tag color='gold'>
                                                                                            Warning
                                                                                        </Tag>
                                                                                    )
                                                                                }
                                                                                {
                                                                                    !traceRow.isMissing &&
                                                                                    !traceRow.isDeleted &&
                                                                                    (!traceRow.warningText || traceRow.warningText.trim() === '') && (
                                                                                        <Tag color='green'>
                                                                                            OK
                                                                                        </Tag>
                                                                                    )
                                                                                }
                                                                            </>
                                                                        )
                                                                    },
                                                                    {
                                                                        title: 'Entity Name',
                                                                        dataIndex: 'entityName',
                                                                        key: 'entityName',
                                                                        width: 240
                                                                    },
                                                                    {
                                                                        title: 'Relationship',
                                                                        dataIndex: 'relationship',
                                                                        key: 'relationship',
                                                                        width: 220
                                                                    },
                                                                    {
                                                                        title: 'Page ID',
                                                                        dataIndex: 'reportPageID',
                                                                        key: 'reportPageID',
                                                                        width: 100,
                                                                        render: (value: number | null) => value ?? 'N/A'
                                                                    },
                                                                    {
                                                                        title: 'Item ID',
                                                                        dataIndex: 'reportItemID',
                                                                        key: 'reportItemID',
                                                                        width: 100,
                                                                        render: (value: number | null) => value ?? 'N/A'
                                                                    },
                                                                    {
                                                                        title: 'Warning',
                                                                        dataIndex: 'warningText',
                                                                        key: 'warningText',
                                                                        width: 280,
                                                                        render: (value: string) => value && value.trim() !== '' ? value : ''
                                                                    }
                                                                ]}
                                                                dataSource={section.rows.map((traceRow, index) => ({
                                                                    ...traceRow,
                                                                    key: `${section.sectionName}-${traceRow.entityType}-${traceRow.entityID ?? 'none'}-${index}`
                                                                }))}
                                                                expandable={{
                                                                    expandedRowRender: renderReportLinkageTraceEntityDetails,
                                                                    rowExpandable: (traceRow) => !!traceRow.entityJson
                                                                }}
                                                                pagination={false}
                                                                rowClassName={() => ''}
                                                                rowKey='key'
                                                                size='small'
                                                                scroll={{ x: 1100 }}
                                                                onRow={(traceRow) => ({
                                                                    style: getReportLinkageTraceRowStyle(traceRow)
                                                                })}
                                                            />
                                                        </>
                                                    )
                                                }]}
                                            />
                                        ))
                                    }
                                </div>
                            </div>
                        )
                        :
                        (
                            <div style={{marginTop: '12px'}}>
                                <Text>
                                    No report linkage trace results available.
                                </Text>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    };

    const renderAIProviderStatusTab = () => {
        return (
            <div style={tabContentLayoutStyle}>
                <div style={leftPanelStyle}>
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22} style={{ textAlign: 'center' }}>
                                <Text underline style={{fontSize: '24px', fontWeight: 'bold'}}>
                                    AI Provider Tools
                                </Text>
                            </Col>
                            <Col span={1} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                            </Col>
                            <Col span={1} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={2} />
                            <Col span={20}>
                                <Text italic style={{float: 'left', fontWeight: 'bold'}}>
                                    Select an AI Provider to begin:
                                </Text>
                            </Col>
                            <Col span={2} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={2} />
                            <Col span={20}>
                                <Form.Item
                                    key='aiproviderexternalvendorid'
                                    name='aiProviderExternalVendorID'
                                    style={{marginBottom: '4px', width: '100%'}}
                                >
                                    <Select
                                        filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                        loading={isGetAIProvidersLoading}
                                        onChange={(option: string) => {
                                            setAIProviderExternalVendorID(option);
                                            setAIProviderStatus(null);
                                            setAISingleTurnPromptResponse(null);
                                            setAIMultiTurnPromptResponse(null);
                                            setAIMultiTurnMessages([]);
                                        }}
                                        placeholder='Select an AI Provider'
                                        showSearch
                                        style={{width: '100%'}}
                                        value={aiProviderExternalVendorID || undefined}
                                    >
                                        {
                                            aiProviders.map((provider) => (
                                                <Option
                                                    key={provider.externalVendorID}
                                                    label={provider.displayName}
                                                    value={provider.externalVendorID.toString()}
                                                >
                                                    {provider.displayName}
                                                </Option>
                                            ))
                                        }
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={2} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                            </Col>
                            <Col span={1} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row' style={{paddingTop: '8px'}}>
                            <Col span={2} />
                            <Col span={20}>
                                <Text italic style={{float: 'left', fontWeight: 'bold'}}>
                                    Provider Check
                                </Text>
                            </Col>
                            <Col span={2} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row' style={{paddingTop: '8px', paddingBottom: '8px'}}>
                            <Col span={2} />
                            <Col span={20}>
                                <Button
                                    disabled={!aiProviderExternalVendorID}
                                    loading={isGetAIProviderStatusLoading}
                                    onClick={() => getAIProviderStatus({
                                        gaps_externalVendorID: aiProviderExternalVendorID
                                    })}
                                    style={{width: '100%', backgroundColor: !aiProviderExternalVendorID ? 'darkgray' : 'cornflowerblue'}}
                                    type='primary'
                                >
                                    Check Provider
                                </Button>
                            </Col>
                            <Col span={2} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Divider style={{ borderTop: '1px solid black', margin: '8px 1px 1px 1px' }}></Divider>
                            </Col>
                            <Col span={1} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row' style={{paddingTop: '8px'}}>
                            <Col span={2} />
                            <Col span={17}>
                                <Text italic style={{float: 'left', fontWeight: 'bold'}}>
                                    Single Request/Response Test
                                </Text>
                            </Col>
                            <Col span={3} style={{textAlign: 'right'}}>
                                <Button
                                    disabled={!aiSingleTurnPrompt}
                                    onClick={() => {
                                        setAISingleTurnPrompt('');
                                        formRetainBIMaintenance.setFieldValue('aiSingleTurnPrompt', '');
                                    }}
                                    size='small'
                                    style={{
                                        backgroundColor: (!aiSingleTurnPrompt) ? 'darkgray' : 'cornflowerblue',
                                        minWidth: '28px',
                                        paddingLeft: '6px',
                                        paddingRight: '6px'
                                    }}
                                    title='Clear Single Request/Response'
                                    type='primary'
                                >
                                    <CloseCircleOutlined />
                                </Button>
                            </Col>
                            <Col span={2} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={2} />
                            <Col span={20}>
                                <Form.Item
                                    key='aisingleturnprompt'
                                    name='aiSingleTurnPrompt'
                                    style={{marginBottom: '4px', width: '100%'}}
                                >
                                    <Input.TextArea
                                        autoSize={{ minRows: 4, maxRows: 8 }}
                                        onChange={(event) => {
                                            setAISingleTurnPrompt(event.target.value);
                                            setAISingleTurnPromptResponse(null);
                                        }}
                                        placeholder='Enter a single request AI test prompt'
                                        value={aiSingleTurnPrompt}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={2} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row' style={{paddingTop: '8px', paddingBottom: '8px'}}>
                            <Col span={2} />
                            <Col span={20}>
                                <Button
                                    disabled={!aiProviderExternalVendorID || !aiSingleTurnPrompt.trim()}
                                    loading={isGetAISingleTurnPromptLoading}
                                    onClick={() => getAISingleTurnPrompt({
                                        externalVendorID: Number(aiProviderExternalVendorID),
                                        tenantID: null,
                                        prompt: aiSingleTurnPrompt
                                    })}
                                    style={{width: '100%', backgroundColor: (!aiProviderExternalVendorID || !aiSingleTurnPrompt.trim()) ? 'darkgray' : 'cornflowerblue'}}
                                    type='primary'
                                >
                                    Run Single Request/Response
                                </Button>
                            </Col>
                            <Col span={2} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <Divider style={{ borderTop: '1px solid black', margin: '8px 1px 1px 1px' }}></Divider>
                            </Col>
                            <Col span={1} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row' style={{paddingTop: '8px'}}>
                            <Col span={2} />
                            <Col span={17}>
                                <Text italic style={{float: 'left', fontWeight: 'bold'}}>
                                    Multi Request/Response (Conversational) Test
                                </Text>
                            </Col>
                            <Col span={3} style={{textAlign: 'right'}}>
                                <Button
                                    disabled={!aiMultiTurnPrompt}
                                    onClick={() => {
                                        setAIMultiTurnPrompt('');
                                        formRetainBIMaintenance.setFieldValue('aiMultiTurnPrompt', '');
                                    }}
                                    size='small'
                                    style={{
                                        backgroundColor: (!aiMultiTurnPrompt) ? 'darkgray' : 'cornflowerblue',
                                        minWidth: '28px',
                                        paddingLeft: '6px',
                                        paddingRight: '6px'
                                    }}
                                    title='Clear Multi Request/Response'
                                    type='primary'
                                >
                                    <CloseCircleOutlined />
                                </Button>
                            </Col>
                            <Col span={2} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={2} />
                            <Col span={20}>
                                <Form.Item
                                    key='aimultiturnprompt'
                                    name='aiMultiTurnPrompt'
                                    style={{marginBottom: '4px', width: '100%'}}
                                >
                                    <Input.TextArea
                                        autoSize={{ minRows: 4, maxRows: 8 }}
                                        onChange={(event) => {
                                            setAIMultiTurnPrompt(event.target.value);
                                            setAIMultiTurnPromptResponse(null);
                                        }}
                                        placeholder='Enter the next multi request AI test prompt'
                                        value={aiMultiTurnPrompt}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={2} />
                        </Row>

                        <Row gutter={16} className='retainbi-compact-row' style={{paddingTop: '8px', paddingBottom: '8px'}}>
                            <Col span={2} />
                            <Col span={20}>
                                <Button
                                    disabled={!aiProviderExternalVendorID || !aiMultiTurnPrompt.trim()}
                                    loading={isGetAIMultiTurnPromptLoading}
                                    onClick={() => {
                                        const nextUserMessage: GetAIMultiTurnPromptMessageModel = {
                                            role: 'user',
                                            content: aiMultiTurnPrompt
                                        };

                                        const nextMessages = [
                                            ...aiMultiTurnMessages,
                                            nextUserMessage
                                        ];

                                        setAIMultiTurnMessages(nextMessages);

                                        getAIMultiTurnPrompt({
                                            externalVendorID: Number(aiProviderExternalVendorID),
                                            tenantID: null,
                                            aiConversationID: null,
                                            messages: nextMessages
                                        });

                                        setAIMultiTurnPrompt('');
                                        formRetainBIMaintenance.setFieldValue('aiMultiTurnPrompt', '');
                                    }}
                                    style={{width: '100%', backgroundColor: (!aiProviderExternalVendorID || !aiMultiTurnPrompt.trim()) ? 'darkgray' : 'cornflowerblue'}}
                                    type='primary'
                                >
                                    Run Multi Request/Response
                                </Button>
                            </Col>
                            <Col span={2} />
                        </Row>
                    </div>
                </div>

                <div style={resultsPanelStyle}>
                    <Text style={{fontSize: '18px', fontWeight: 'bold'}}>
                        AI Provider Tools Results
                    </Text>

                    <div style={resultsSummaryAreaStyle}>
                        <Text italic style={{fontWeight: 'bold'}}>
                            Provider Check Response
                        </Text>

                        {
                            aiProviderStatus
                            ?
                            (
                                <>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={7} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Provider Name:
                                        </Col>
                                        <Col span={17}>
                                            <Text>{aiProviderStatus.name || 'N/A'}</Text>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={7} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            External Vendor ID:
                                        </Col>
                                        <Col span={17}>
                                            <Text>{aiProviderStatus.externalVendorID ?? 'N/A'}</Text>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={7} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Credential Tenant ID:
                                        </Col>
                                        <Col span={17}>
                                            <Text>{aiProviderStatus.credentialTenantID ?? 'Global'}</Text>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={7} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            API Key:
                                        </Col>
                                        <Col span={17}>
                                            <Tag color={aiProviderStatus.hasAPIKey ? 'green' : 'red'}>
                                                {aiProviderStatus.hasAPIKey ? 'Configured' : 'Missing'}
                                            </Tag>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={7} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            URL:
                                        </Col>
                                        <Col span={17}>
                                            <Tag color={aiProviderStatus.hasURL ? 'green' : 'default'}>
                                                {aiProviderStatus.hasURL ? 'Configured' : 'Not Set'}
                                            </Tag>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={7} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Secret:
                                        </Col>
                                        <Col span={17}>
                                            <Tag color={aiProviderStatus.hasSecret ? 'green' : 'default'}>
                                                {aiProviderStatus.hasSecret ? 'Configured' : 'Not Set'}
                                            </Tag>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={7} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Vars:
                                        </Col>
                                        <Col span={17}>
                                            <Tag color={aiProviderStatus.hasVars ? 'green' : 'default'}>
                                                {aiProviderStatus.hasVars ? 'Configured' : 'Not Set'}
                                            </Tag>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={7} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Message:
                                        </Col>
                                        <Col span={17}>
                                            <Text>{aiProviderStatus.message}</Text>
                                        </Col>
                                    </Row>
                                </>
                            )
                            :
                            (
                                <div style={{marginTop: '8px'}}>
                                    <Text>No AI Provider Check Response Available.</Text>
                                </div>
                            )
                        }

                        <Divider style={{ borderTop: '1px solid black', margin: '12px 1px 8px 1px' }}></Divider>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Text italic style={{float: 'left', fontWeight: 'bold'}}>
                                    Single Request/Response Test Response
                                </Text>
                            </Col>
                            <Col span={3} style={{textAlign: 'right'}}>
                                <Button
                                    disabled={!aiSingleTurnPromptResponse}
                                    onClick={() => {
                                        setAISingleTurnPromptResponse(null);
                                        resetGetAISingleTurnPrompt();
                                    }}
                                    size='small'
                                    style={{
                                        backgroundColor: !aiSingleTurnPromptResponse ? 'darkgray' : 'cornflowerblue',
                                        minWidth: '28px',
                                        paddingLeft: '6px',
                                        paddingRight: '6px'
                                    }}
                                    title='Clear Single Request/Response Response'
                                    type='primary'
                                >
                                    <CloseCircleOutlined />
                                </Button>
                            </Col>
                        </Row>

                        {
                            aiSingleTurnPromptResponse
                            ?
                            (
                                <>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={6} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Success:
                                        </Col>
                                        <Col span={18}>
                                            <Tag color={aiSingleTurnPromptResponse.success ? 'green' : 'red'}>
                                                {aiSingleTurnPromptResponse.success ? 'Yes' : 'No'}
                                            </Tag>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={6} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Status Code:
                                        </Col>
                                        <Col span={18}>
                                            <Text>{aiSingleTurnPromptResponse.statusCode ?? 'N/A'}</Text>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={6} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Message:
                                        </Col>
                                        <Col span={18}>
                                            <Text>{aiSingleTurnPromptResponse.message}</Text>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={6} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Response:
                                        </Col>
                                        <Col span={18}>
                                            <Text style={{whiteSpace: 'pre-wrap'}}>
                                                {aiSingleTurnPromptResponse.text || 'N/A'}
                                            </Text>
                                        </Col>
                                    </Row>
                                </>
                            )
                            :
                            (
                                <div style={{marginTop: '8px'}}>
                                    <Text>No Single Request/Response Test Response Available.</Text>
                                </div>
                            )
                        }

                        <Divider style={{ borderTop: '1px solid black', margin: '12px 1px 8px 1px' }}></Divider>

                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Text italic style={{float: 'left', fontWeight: 'bold'}}>
                                    Multi Request/Response Test Response Transcript
                                </Text>
                            </Col>
                            <Col span={3} style={{textAlign: 'right'}}>
                                <Button
                                    disabled={aiMultiTurnMessages.length === 0 && !aiMultiTurnPromptResponse}
                                    onClick={() => {
                                        setAIMultiTurnPromptResponse(null);
                                        setAIMultiTurnMessages([]);
                                        resetGetAIMultiTurnPrompt();
                                    }}
                                    size='small'
                                    style={{
                                        backgroundColor: (aiMultiTurnMessages.length === 0 && !aiMultiTurnPromptResponse) ? 'darkgray' : 'cornflowerblue',
                                        minWidth: '28px',
                                        paddingLeft: '6px',
                                        paddingRight: '6px'
                                    }}
                                    title='Clear Multi Request/Response Transcript'
                                    type='primary'
                                >
                                    <CloseCircleOutlined />
                                </Button>
                            </Col>
                        </Row>

                        {
                            aiMultiTurnPromptResponse
                            &&
                            (
                                <>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={6} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Success:
                                        </Col>
                                        <Col span={18}>
                                            <Tag color={aiMultiTurnPromptResponse.success ? 'green' : 'red'}>
                                                {aiMultiTurnPromptResponse.success ? 'Yes' : 'No'}
                                            </Tag>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={6} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Status Code:
                                        </Col>
                                        <Col span={18}>
                                            <Text>{aiMultiTurnPromptResponse.statusCode ?? 'N/A'}</Text>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} className='retainbi-compact-row'>
                                        <Col span={6} style={{paddingTop: '4px', textAlign: 'right'}}>
                                            Message:
                                        </Col>
                                        <Col span={18}>
                                            <Text>{aiMultiTurnPromptResponse.message}</Text>
                                        </Col>
                                    </Row>
                                </>
                            )
                        }

                        {
                            aiMultiTurnMessages.length > 0
                            ?
                            (
                                <div style={{border: '1px solid #d9d9d9', borderRadius: '4px', marginTop: '8px', maxHeight: '320px', overflowY: 'auto', padding: '6px'}}>
                                    {
                                        aiMultiTurnMessages.map((message, index) => (
                                            <div
                                                key={`${message.role}-${index}`}
                                                style={{
                                                    backgroundColor: message.role === 'assistant' ? '#f5f5f5' : 'white',
                                                    borderBottom: index < aiMultiTurnMessages.length - 1 ? '1px solid #d9d9d9' : 'none',
                                                    padding: '8px',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                <Text strong>{message.role === 'assistant' ? 'Assistant' : 'User'}</Text>
                                                <br />
                                                <Text style={{whiteSpace: 'pre-wrap'}}>
                                                    {message.content || 'N/A'}
                                                </Text>
                                            </div>
                                        ))
                                    }
                                </div>
                            )
                            :
                            (
                                <div style={{marginTop: '8px'}}>
                                    <Text>No Multi Request/Response Test Response Transcript Available.</Text>
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        );
    };

    const determineWhichError = () => {
        if (isGetReportsError) {
            return currentError + ' ' + ((getReportsError as any)?.response?.data?.message ?? '');
        }
        else if (isGetChartsError) {
            return currentError + ' ' + ((getChartsError as any)?.response?.data?.message ?? '');
        }
        else if (isGetDataSetsError) {
            return currentError + ' ' + ((getDataSetsError as any)?.response?.data?.message ?? '');
        }
        else if (isGetTenantsError) {
            return currentError + ' ' + ((getTenantsError as any)?.response?.data?.message ?? '');
        }
        else if (isGetFTPServersError) {
            return currentError + ' ' + ((getFTPServersError as any)?.response?.data?.message ?? '');
        }
        else if (isGetBITargetPurgeSummaryError) {
            return currentError + ' ' + ((getBITargetPurgeSummaryError as any)?.response?.data?.message ?? '');
        }
        else if (isGetBIRecordCleanupLocatorSummaryError) {
            return currentError + ' ' + ((getBIRecordCleanupLocatorSummaryError as any)?.response?.data?.message ?? '');
        }
        else if (isGetBIReportDataSetInputScanSummaryError) {
            return currentError + ' ' + ((getBIReportDataSetInputScanSummaryError as any)?.response?.data?.message ?? '');
        }
        else if (isGetReportLinkageTraceRowsError) {
            return currentError + ' ' + ((getReportLinkageTraceRowsError as any)?.response?.data?.message ?? '');
        }
        else if (isGetAIProviderStatusError) {
            return currentError + ' ' + ((getAIProviderStatusError as any)?.response?.data?.message ?? '');
        }
        else if (isGetAIProvidersError) {
            return currentError + ' ' + ((getAIProvidersError as any)?.response?.data?.message ?? '');
        }
        else if (isGetAISingleTurnPromptError) {
            return currentError + ' ' + ((getAISingleTurnPromptError as any)?.response?.data?.message ?? '');
        }
        else if (isGetAIMultiTurnPromptError) {
            return currentError + ' ' + ((getAIMultiTurnPromptError as any)?.response?.data?.message ?? '');
        }
        else {
            return '';
        }
    }

    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <Spin spinning={
                            isGetReportsLoading ||
                            isGetTenantsLoading ||
                            isGetChartsLoading ||
                            isGetDataSetsLoading ||
                            isGetFTPServersLoading ||
                            isGetReportLinkageTraceRowsLoading ||
                            isGetAIProviderStatusLoading ||
                            isGetAIProvidersLoading ||
                            isGetAISingleTurnPromptLoading ||
                            isGetAIMultiTurnPromptLoading
                           }>
                <Space direction="horizontal" style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>RetainBI Maintenance</Title>
                    </Text>
                </Space>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetReportsError ||
                        isGetChartsError ||
                        isGetDataSetsError ||
                        isGetTenantsError ||
                        isGetFTPServersError ||
                        isGetBITargetPurgeSummaryError ||
                        isGetBIRecordCleanupLocatorSummaryError ||
                        isGetBIReportDataSetInputScanSummaryError ||
                        isGetReportLinkageTraceRowsError ||
                        isGetAIProviderStatusError ||
                        isGetAIProvidersError ||
                        isGetAISingleTurnPromptError ||
                        isGetAIMultiTurnPromptError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Form
                    form={formRetainBIMaintenance}
                    key='retainBIMaintenance'
                    onFinishFailed={(error) => console.log(error)}
                >
                    <Tabs
                        activeKey={activeMaintenanceTab}
                        animated={false}
                        type='card'
                        onChange={(key) => {
                            resetGetBITargetPurgeSummary();
                            resetGetBIRecordCleanupLocatorSummary();
                            resetGetBIReportDataSetInputScanSummary();
                            resetGetReportLinkageTraceRows();
                            resetGetAIProviderStatus();
                            resetGetAISingleTurnPrompt();
                            resetGetAIMultiTurnPrompt();
                            setAISingleTurnPromptResponse(null);
                            setAIMultiTurnPromptResponse(null);
                            setAIMultiTurnMessages([]);
                            setCurrentError('');
                            setActiveMaintenanceTab(key as RetainBIMaintenanceTabKey);
                        }}
                        items={[
                            {
                                key: 'target-purge',
                                label: 'BI Target Purge',
                                children: renderTargetPurgeTab()
                            },
                            {
                                key: 'record-cleanup',
                                label: 'Record Cleanup Locator',
                                children: renderRecordCleanupTab()
                            },
                            {
                                key: 'report-dataset-input-scanning',
                                label: 'Report / Dataset Input Scanning',
                                children: renderReportDataSetInputScanningTab()
                            },
                            {
                                key: 'report-linkage-trace',
                                label: 'Report Linkage Trace',
                                children: renderReportLinkageTraceTab()
                            },
                            {
                                key: 'ai-provider',
                                label: 'AI Provider',
                                children: renderAIProviderStatusTab()
                            }
                        ]}
                    />
                </Form>
            </Spin>
        </Card>
    );
};

export default RetainBIMaintenance;
